import React, { useState, useEffect, useMemo, useCallback, useRef, createContext, useContext } from 'react';
import PrivacyPage from './pages/PrivacyPage';
import RefundPage from './pages/RefundPage';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import './index.css';
// API Base URL
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// === Missing utilities ===
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
// Same-origin log helper — only prints in development to avoid leaking
// emails / IDs into production browser logs.

const devLog = (...args) => {
  if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.log(...args);
  }
};
// Open a player WebSocket using the `player-token` subprotocol so the token
// doesn't leak via URL / referrer / proxy logs. Falls back to the legacy
// `?token=` query for the tiny (mostly mobile Safari) minority where
// subprotocol injection isn't surfaced cleanly.

const describeError = async (err, res) => {
  if (err) {
    if (err instanceof TypeError) return 'Network error — check your connection.';
    return err.message || 'Something went wrong.';
  }
  if (!res) return 'Something went wrong.';
  let detail = '';
  try { detail = (await res.json()).detail || ''; } catch { /* no body */ }
  if (res.status === 401) return detail || 'Please sign in again.';
  if (res.status === 403) return detail || "You don't have permission to do that.";
  if (res.status === 404) return detail || 'That resource was not found.';
  if (res.status === 408) return detail || 'The request took too long. Try again with a smaller input.';
  if (res.status === 429) return detail || 'Too many requests. Slow down and try again.';
  if (res.status >= 500) return detail || 'Server error. Please try again shortly.';
  return detail || `Request failed (${res.status}).`;
};
// Sound Effects Context & Hook

// === Sound system ===
const SoundContext = createContext();

const SoundProvider = ({ children }) => {
  const [muted, setMuted] = useState(() => localStorage.getItem('sound_muted') === 'true');
  const ctxRef = useRef(null);
  const getCtx = useCallback(() => {
    if (!ctxRef.current || ctxRef.current.state === 'closed') {
      ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (ctxRef.current.state === 'suspended') ctxRef.current.resume();
    return ctxRef.current;
  }, []);
  const playTone = useCallback((freq, duration, type = 'sine', gain = 0.3) => {
    if (muted) return;
    try {
      const ctx = getCtx();
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = type;
      osc.frequency.setValueAtTime(freq, ctx.currentTime);
      g.gain.setValueAtTime(gain, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
      osc.connect(g);
      g.connect(ctx.destination);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + duration);
    } catch {}
  }, [muted, getCtx]);
  const playCorrect = useCallback(() => {
    if (muted) return;
    playTone(523, 0.12, 'sine', 0.25);
    setTimeout(() => playTone(659, 0.12, 'sine', 0.25), 100);
    setTimeout(() => playTone(784, 0.2, 'sine', 0.3), 200);
  }, [muted, playTone]);
  const playWrong = useCallback(() => {
    if (muted) return;
    playTone(300, 0.15, 'sawtooth', 0.2);
    setTimeout(() => playTone(250, 0.25, 'sawtooth', 0.15), 120);
  }, [muted, playTone]);
  const playCountdownBeep = useCallback(() => {
    playTone(880, 0.1, 'sine', 0.2);
  }, [playTone]);
  const playCountdownGo = useCallback(() => {
    playTone(1047, 0.3, 'sine', 0.35);
  }, [playTone]);
  const playStreak = useCallback((level) => {
    if (muted) return;
    const base = 600 + level * 100;
    playTone(base, 0.1, 'sine', 0.2);
    setTimeout(() => playTone(base + 200, 0.1, 'sine', 0.25), 80);
    setTimeout(() => playTone(base + 400, 0.15, 'sine', 0.3), 160);
  }, [muted, playTone]);
  const playVictory = useCallback(() => {
    if (muted) return;
    const notes = [523, 659, 784, 1047, 784, 1047];
    notes.forEach((n, i) => setTimeout(() => playTone(n, 0.2, 'sine', 0.25), i * 150));
  }, [muted, playTone]);
  const playTick = useCallback(() => {
    playTone(1000, 0.05, 'sine', 0.1);
  }, [playTone]);
  const playPlayerJoin = useCallback(() => {
    playTone(700, 0.08, 'sine', 0.15);
    setTimeout(() => playTone(900, 0.1, 'sine', 0.15), 80);
  }, [playTone]);
  const toggleMute = useCallback(() => {
    setMuted(prev => {
      localStorage.setItem('sound_muted', !prev);
      return !prev;
    });
  }, []);
  return (
    <SoundContext.Provider value={{ muted, toggleMute, playCorrect, playWrong, playCountdownBeep, playCountdownGo, playStreak, playVictory, playTick, playPlayerJoin }}>
      {children}
    </SoundContext.Provider>
  );
};

export const useSound = () => useContext(SoundContext);

// === MuteButton ===
const MuteButton = () => {
  const { muted, toggleMute } = useSound();
  const label = muted ? 'Unmute sounds' : 'Mute sounds';
  return (
    <button
      type="button"
      onClick={toggleMute}
      className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
      title={label}
      aria-label={label}
      aria-pressed={muted}
      data-testid="mute-toggle"
    >
      <span aria-hidden="true">{muted ? '🔇' : '🔊'}</span>
    </button>
  );
};

// === Confetti ===
const Confetti = ({ active, duration = 3000 }) => {
  const canvasRef = useRef(null);
  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const colors = ['#E21B3C', '#1368CE', '#D89E00', '#26890C', '#8E24AA', '#FF6B00', '#FFD700', '#FF69B4'];
    const particles = Array.from({ length: 150 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height - canvas.height,
      w: Math.random() * 10 + 5,
      h: Math.random() * 6 + 3,
      color: colors[Math.floor(Math.random() * colors.length)],
      vx: (Math.random() - 0.5) * 4,
      vy: Math.random() * 3 + 2,
      rotation: Math.random() * 360,
      rotSpeed: (Math.random() - 0.5) * 10,
    }));
    let frame;
    const start = Date.now();
    const animate = () => {
      const elapsed = Date.now() - start;
      if (elapsed > duration) { ctx.clearRect(0, 0, canvas.width, canvas.height); return; }
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const opacity = elapsed > duration - 500 ? (duration - elapsed) / 500 : 1;
      particles.forEach(p => {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.05;
        p.rotation += p.rotSpeed;
        ctx.save();
        ctx.globalAlpha = opacity;
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      });
      frame = requestAnimationFrame(animate);
    };
    animate();
    return () => cancelAnimationFrame(frame);
  }, [active, duration]);
  if (!active) return null;
  return <canvas ref={canvasRef} className="fixed inset-0 pointer-events-none z-50" />;
};

// === QuizShareView ===
const QuizShareView = () => {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const quizId = window.location.pathname.split('/quiz/')[1];
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const fetchQuiz = async () => {
      try {
        const res = await fetch(`${API_URL}/api/quizzes/${quizId}`);
        if (res.ok) setQuiz(await res.json());
      } catch {} finally { setLoading(false); }
    };
    fetchQuiz();
  }, [quizId]);
  if (loading) return <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0f0529]' : 'bg-[#1a0d3e]'}`}><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500" /></div>;
  if (!quiz) return <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0f0529]' : 'bg-[#1a0d3e]'}`}><p className="text-red-500">Quiz not found</p></div>;
  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0f0529]' : 'bg-gradient-to-br from-[#1a0d3e] via-[#2d1b69] to-[#0f0529]'} py-16 px-4`}>
      <div className="max-w-lg mx-auto">
        <div className={`${isDark ? 'bg-[#241651]' : 'bg-[#241651]'} rounded-3xl p-8 shadow-2xl text-center`}>
          <div className="text-5xl mb-4">📝</div>
          <h1 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-white'}`}>{quiz.title}</h1>
          <p className={`mb-2 ${isDark ? 'text-purple-300' : 'text-purple-300'}`}>{quiz.questions?.length || 0} questions</p>
          <p className={`text-sm mb-6 ${isDark ? 'text-purple-300' : 'text-purple-300'}`}>by {quiz.creator_name || 'Anonymous'} • {quiz.play_count || 0} plays</p>
          <button
            onClick={() => user ? navigate(`/host/${quizId}`) : navigate('/auth')}
            className="w-full py-4 bg-gradient-to-r from-violet-500 to-purple-600 text-white font-bold text-xl rounded-xl hover:scale-105 transition-transform"
          >
            {user ? 'Host This Quiz' : 'Sign In to Host'}
          </button>
        </div>
      </div>
    </div>
  );
};


// Theme Context
const ThemeContext = createContext();
export const useTheme = () => useContext(ThemeContext);
const ThemeProvider = ({ children }) => {
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved === 'dark' || (!saved && window.matchMedia('(prefers-color-scheme: dark)').matches);
  });
  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark);
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
  }, [isDark]);
  const themeValue = useMemo(() => ({ isDark, toggleTheme: () => setIsDark(prev => !prev) }), [isDark]);
  return (
    <ThemeContext.Provider value={themeValue}>
      {children}
    </ThemeContext.Provider>
  );
};
// Auth Context
const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);
// Helper to make authenticated API calls via httpOnly session cookie
const authFetch = (url, options = {}) => {
  return fetch(url, { ...options, credentials: 'include' });
};
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    checkAuth();
  }, []);
  const checkAuth = async () => {
    try {
      // Clean up any legacy localStorage token from previous sessions
      localStorage.removeItem('session_token');
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        return userData;
      }
    } catch (error) {
      console.error('Auth check failed:', error.message);
    } finally {
      setLoading(false);
    }
    return null;
  };
  const loginWithGoogle = () => {
    window.location.href = `${API_URL}/api/auth/google/login`;
  };
  // Bug #1 fix: server now returns `session_token` in the body; we only
  // store it when present (and never write the literal undefined).
  const storeSessionToken = (data) => {
    if (data && typeof data.session_token === 'string' && data.session_token.length > 10) {
      localStorage.setItem('session_token', data.session_token);
    }
  };
  const loginWithEmail = async (email, password) => {
    const res = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
      credentials: 'include'
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.detail || 'Login failed');
    }
    setUser(data);
    return data;
  };
  const signup = async (email, password, name) => {
    const res = await fetch(`${API_URL}/api/auth/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name }),
      credentials: 'include'
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.detail || 'Signup failed');
    }
    setUser(data);
    return data;
  };
  const logout = async () => {
    await fetch(`${API_URL}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include'
    });
    setUser(null);
  };
  const authValue = useMemo(() => ({ user, setUser, loginWithGoogle, loginWithEmail, signup, logout, loading, checkAuth }), [user, loading]);
  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  );
};
// Auth Callback Component - handles redirect after Google OAuth (cookie is already set by backend)
const AuthCallback = () => {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const processedRef = React.useRef(false);
  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;
    // Clean up any legacy localStorage tokens
    localStorage.removeItem('session_token');
    // The httpOnly cookie was set by the backend redirect — just validate the session
    fetch(`${API_URL}/api/auth/me`, { credentials: 'include' })
      .then(res => {
        if (!res.ok) throw new Error('Session not valid');
        return res.json();
      })
      .then(data => {
        setUser(data);
        window.history.replaceState(null, '', '/dashboard');
        navigate('/dashboard', { replace: true });
      })
      .catch((err) => {
        console.error('Auth callback error:', err.message);
        navigate('/auth', { replace: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#1a0d3e] via-[#2d1b69] to-[#0f0529]">
      <div className="text-white text-xl">Signing you in...</div>
    </div>
  );
};
// Auth Page Component - Login/Signup
const AuthPage = () => {
  const { isDark } = useTheme();
  const { loginWithGoogle, loginWithEmail, signup } = useAuth();
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const trimmedEmail = email.trim();
    if (!EMAIL_REGEX.test(trimmedEmail)) {
      setError('Please enter a valid email address (e.g. you@example.com).');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (!isLogin && name.trim().length < 2) {
      setError('Please tell us your name (2+ characters).');
      return;
    }
    setLoading(true);
    try {
      if (isLogin) {
        await loginWithEmail(trimmedEmail, password);
      } else {
        await signup(trimmedEmail, password, name.trim());
      }
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(await describeError(err));
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0f0529]' : 'bg-gradient-to-br from-[#1a0d3e] via-[#2d1b69] to-[#0f0529]'} px-4`}>
      <div className={`max-w-md w-full ${isDark ? 'bg-[#241651]' : 'bg-[#241651]'} rounded-3xl shadow-2xl p-8`}>
        {/* Logo */}
        <div className="text-center mb-8">
          <button onClick={() => navigate('/')} className="inline-block">
            <span className="text-3xl font-bold bg-gradient-to-r from-amber-300 to-yellow-400 bg-clip-text text-transparent">
              KwizHub
            </span>
          </button>
          <p className={`mt-2 ${isDark ? 'text-purple-300' : 'text-purple-300'}`}>
            {isLogin ? 'Welcome back!' : 'Create your account'}
          </p>
        </div>
        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-500/20 border border-red-400 text-red-300 rounded-xl text-sm">
            {error}
          </div>
        )}
        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-purple-200' : 'text-purple-100'}`}>
                Full Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Doe"
                className={`w-full px-4 py-3 rounded-xl border ${isDark ? 'bg-[#352770] border-[#3b2580] text-white' : 'bg-[#1a0d3e] border-[#3b2580]'} focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none`}
                required={!isLogin}
                data-testid="auth-name-input"
              />
            </div>
          )}
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-purple-200' : 'text-purple-100'}`}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className={`w-full px-4 py-3 rounded-xl border ${isDark ? 'bg-[#352770] border-[#3b2580] text-white' : 'bg-[#1a0d3e] border-[#3b2580]'} focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none`}
              required
              autoComplete={isLogin ? 'email' : 'username'}
              inputMode="email"
              pattern="[^\s@]+@[^\s@]+\.[^\s@]{2,}"
              data-testid="auth-email-input"
            />
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-purple-200' : 'text-purple-100'}`}>
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className={`w-full px-4 py-3 rounded-xl border ${isDark ? 'bg-[#352770] border-[#3b2580] text-white' : 'bg-[#1a0d3e] border-[#3b2580]'} focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none`}
              required
              minLength={8}
              autoComplete={isLogin ? 'current-password' : 'new-password'}
              data-testid="auth-password-input"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-gradient-to-r from-violet-500 to-purple-600 text-white font-semibold rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
            data-testid="auth-submit-btn"
          >
            {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
          </button>
        </form>
        {/* Divider */}
        <div className="flex items-center my-6">
          <div className={`flex-1 border-t ${isDark ? 'border-[#352770]' : 'border-[#3b2580]'}`}></div>
          <span className={`px-4 text-sm ${isDark ? 'text-purple-300' : 'text-purple-300'}`}>or</span>
          <div className={`flex-1 border-t ${isDark ? 'border-[#352770]' : 'border-[#3b2580]'}`}></div>
        </div>
        {/* Google Sign In */}
        <button
          onClick={loginWithGoogle}
          className={`w-full py-3 flex items-center justify-center gap-3 border-2 rounded-xl font-medium transition-colors ${isDark ? 'border-[#3b2580] text-white hover:bg-[#352770]' : 'border-[#3b2580] text-purple-100 hover:bg-[#1a0d3e]'}`}
          data-testid="google-signin-btn"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>
        {/* Toggle Login/Signup */}
        <p className={`mt-6 text-center text-sm ${isDark ? 'text-purple-300' : 'text-purple-300'}`}>
          {isLogin ? "Don't have an account? " : "Already have an account? "}
          <button
            onClick={() => { setIsLogin(!isLogin); setError(''); }}
            className="text-purple-500 font-medium hover:underline"
            data-testid="auth-toggle-btn"
          >
            {isLogin ? 'Sign Up' : 'Sign In'}
          </button>
        </p>
      </div>
    </div>
  );
};
// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#352770] dark:bg-[#0f0529]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }
  return children;
};
// HomePage Component
const HomePage = () => {
  const { user } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-[#0f0529]' : 'bg-gradient-to-br from-[#1a0d3e] via-[#2d1b69] to-[#0f0529]'}`}>
      {/* Navbar */}
      <nav className="glass fixed w-full top-0 z-50 border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <span className="text-3xl font-bold bg-gradient-to-r from-amber-300 to-yellow-400 bg-clip-text text-transparent">
              KwizHub
            </span>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/pricing')}
              className="px-4 py-2 text-white/90 hover:text-white font-medium transition-colors"
              data-testid="nav-pricing-btn"
            >
              Pricing
            </button>
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
              data-testid="theme-toggle"
            >
              {isDark ? '☀️' : '🌙'}
            </button>
            {user ? (
              <button
                onClick={() => navigate('/dashboard')}
                className="px-6 py-2 bg-[#241651] text-purple-300 font-semibold rounded-full hover:shadow-lg transition-all"
                data-testid="dashboard-btn"
              >
                Dashboard
              </button>
            ) : (
              <button
                onClick={() => navigate('/auth')}
                className="px-6 py-2 bg-[#241651] text-purple-300 font-semibold rounded-full hover:shadow-lg transition-all"
                data-testid="login-btn"
              >
                Sign In
              </button>
            )}
          </div>
        </div>
      </nav>
      {/* Hero Section */}
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center py-20">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 drop-shadow-lg">
              Make Learning
              <span className="block text-yellow-300">Awesome!</span>
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-10 max-w-2xl mx-auto">
              Create quizzes from PDFs using AI, host live games, and compete with friends in real-time!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/join')}
                className="px-8 py-4 bg-[#241651] text-purple-300 font-bold text-xl rounded-2xl hover:scale-105 transition-transform shadow-xl"
                data-testid="join-game-btn"
              >
                Join a Game
              </button>
              <button
                onClick={() => user ? navigate('/create-options') : navigate('/auth')}
                className="px-8 py-4 bg-yellow-400 text-[#1a0d3e] font-bold text-xl rounded-2xl hover:bg-yellow-300 hover:scale-105 transition-all shadow-xl shadow-yellow-400/30"
                data-testid="create-quiz-btn"
              >
                Create Quiz
              </button>
            </div>
          </div>
          {/* Features */}
          <div className="grid md:grid-cols-3 gap-8 mt-16">
            {[
              { icon: '📄', title: 'Upload PDF', desc: 'Upload any PDF and let AI generate quiz questions automatically' },
              { icon: '🎮', title: 'Live Games', desc: 'Host real-time multiplayer quiz competitions!' },
              { icon: '🏆', title: 'Compete', desc: 'Earn points, climb leaderboards, and become the champion' }
            ].map((feature) => (
              <div key={feature.title} className="glass rounded-2xl p-8 text-center hover:scale-105 transition-transform">
                <div className="text-5xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold text-white dark:text-white mb-2">{feature.title}</h3>
                <p className="text-purple-200 dark:text-purple-200">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};
// Create Quiz Options Page
const CreateQuizOptions = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const options = [
    {
      icon: '✏️',
      title: 'Create Manually',
      description: 'Build your quiz question by question with full control over content',
      color: 'from-violet-500 to-purple-600',
      path: '/create',
      features: ['Custom questions', 'Set time limits', 'Add explanations']
    },
    {
      icon: '📄',
      title: 'Upload PDF',
      description: 'Upload a document and let AI generate quiz questions automatically',
      color: 'from-blue-500 to-cyan-500',
      path: '/upload',
      features: ['AI-powered', 'Supports any PDF', 'Up to 30 questions']
    },
    {
      icon: '🤖',
      title: 'AI from Topic',
      description: 'Enter any topic and AI will create a complete quiz instantly',
      color: 'from-orange-500 to-red-500',
      path: '/ai-generate',
      features: ['Instant generation', '10 questions', 'Multiple difficulties']
    }
  ];
  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0f0529]' : 'bg-gradient-to-br from-[#1a0d3e] via-[#2d1b69] to-[#0f0529]'} py-12 px-4`}>
      <div className="max-w-5xl mx-auto">
        <button onClick={() => navigate(-1)} className="text-white mb-8 hover:underline flex items-center">
          ← Back
        </button>
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Create a Quiz
          </h1>
          <p className="text-xl text-white/80">
            Choose how you'd like to create your quiz
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {options.map((option) => (
            <button
              key={option.path}
              onClick={() => navigate(option.path)}
              className={`bg-gradient-to-br ${option.color} rounded-3xl p-8 text-left text-white hover:scale-105 transition-all shadow-2xl group`}
              data-testid={`create-option-${option.path}`}
            >
              <div className="text-5xl mb-4">{option.icon}</div>
              <h2 className="text-2xl font-bold mb-2">{option.title}</h2>
              <p className="text-white/80 mb-6 text-sm">{option.description}</p>
              <ul className="space-y-2 mb-6">
                {option.features.map((feature, j) => (
                  <li key={`${option.path}-feat-${j}`} className="flex items-center text-sm text-white/70">
                    <span className="mr-2">✓</span> {feature}
                  </li>
                ))}
              </ul>
              <div className="flex items-center text-sm font-medium group-hover:translate-x-2 transition-transform">
                Get Started <span className="ml-2">→</span>
              </div>
            </button>
          ))}
        </div>
        {/* Quick tip */}
        <div className={`mt-12 ${isDark ? 'bg-[#241651]/70' : 'bg-white/20'} backdrop-blur-lg rounded-2xl p-6 text-center`}>
          <p className="text-white">
            💡 <span className="font-medium">Tip:</span> Not sure which to choose? 
            <span className="text-yellow-300 font-medium"> AI from Topic</span> is the fastest way to get started!
          </p>
        </div>
      </div>
    </div>
  );
};
// Dashboard Component
const Dashboard = () => {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tierInfo, setTierInfo] = useState(null);
  const [showCreditsBanner, setShowCreditsBanner] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const duplicateQuiz = async (quizId) => {
    try {
      const res = await authFetch(`${API_URL}/api/quizzes/${quizId}/duplicate`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        fetchQuizzes();
      }
    } catch (err) { console.error('Failed to duplicate:', err); }
  };
  const shareQuiz = (quizId) => {
    const url = `${window.location.origin}/quiz/${quizId}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(quizId);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };
  useEffect(() => {
    fetchQuizzes();
    fetchTierInfo();
  }, []);

  // Google Ads purchase-conversion event — fires once when Lemon Squeezy
  // redirects here with ?payment=success. Strips the param afterwards so a
  // page reload doesn't double-count.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') !== 'success') return;
    if (typeof window.gtag !== 'function') return;
    const txnId = params.get('order_id') || params.get('transaction_id') || '';
    window.gtag('event', 'conversion', {
      send_to: 'AW-18133084915/heLgCKSX_qUcEPPVw8ZD',
      value: 1.0,
      currency: 'INR',
      transaction_id: txnId,
    });
    // Remove the marker so the effect won't re-fire on reload / navigation
    params.delete('payment');
    const newUrl = window.location.pathname + (params.toString() ? `?${params}` : '');
    window.history.replaceState({}, '', newUrl);
  }, []);
  const fetchQuizzes = async () => {
    try {
      // Fetch user's own quizzes
      const res = await authFetch(`${API_URL}/api/quizzes/my`);
      const data = await res.json();
      setQuizzes(data.quizzes || []);
    } catch (error) {
      console.error('Failed to fetch quizzes:', error);
    } finally {
      setLoading(false);
    }
  };
  const fetchTierInfo = async () => {
    try {
      const res = await authFetch(`${API_URL}/api/account/tier`);
      if (res.ok) {
        const data = await res.json();
        setTierInfo(data);
        // Show banner if free tier and 1 or fewer credits remaining
        if (data.effective_tier === 'free' && !data.limits?.ai_topic_unlimited && data.credits?.ai_topic <= 1) {
          setShowCreditsBanner(true);
        }
      }
    } catch (error) {
      console.error('Failed to fetch tier info:', error);
    }
  };
  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0f0529]' : 'bg-[#1a0d3e]'}`}>
      {/* Credits Low Banner */}
      {showCreditsBanner && (
        <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 px-4" data-testid="credits-low-banner">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">⚡</span>
              <div>
                <span className="font-semibold">
                  {tierInfo?.credits?.ai_topic === 0 
                    ? "You're out of AI quiz credits!" 
                    : "Only 1 AI quiz credit remaining!"}
                </span>
                <span className="ml-2 opacity-90">
                  Upgrade to Pro for unlimited AI quizzes.
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/pricing')}
                className="px-4 py-1.5 bg-[#241651] text-orange-400 font-semibold rounded-full hover:bg-[#352770] transition-colors"
                data-testid="upgrade-banner-btn"
              >
                Upgrade Now
              </button>
              <button
                onClick={() => setShowCreditsBanner(false)}
                className="text-white/80 hover:text-white"
                data-testid="close-banner-btn"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Navbar */}
      <nav className={`${isDark ? 'bg-[#241651]' : 'bg-[#241651]'} shadow-md`}>
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <button onClick={() => navigate('/')} className="text-2xl font-bold bg-gradient-to-r from-amber-300 to-yellow-400 bg-clip-text text-transparent">
            KwizHub
          </button>
          <div className="flex items-center space-x-4">
            {user?.is_admin && (
              <button
                onClick={() => navigate('/admin')}
                className="px-4 py-2 bg-yellow-500 text-[#1a0d3e] rounded-lg hover:bg-yellow-600"
                data-testid="admin-btn"
              >
                Admin
              </button>
            )}
            <button
              onClick={() => navigate('/pricing')}
              className={`px-4 py-2 rounded-lg ${isDark ? 'text-purple-400 hover:bg-[#352770]' : 'text-purple-300 hover:bg-[#352770]'}`}
              data-testid="dashboard-pricing-btn"
            >
              Pricing
            </button>
            <button
              onClick={() => navigate('/account')}
              className={`px-4 py-2 rounded-lg ${isDark ? 'bg-[#352770] text-white hover:bg-[#352770]' : 'bg-[#352770] text-purple-100 hover:bg-[#352770]'}`}
              data-testid="account-btn"
            >
              Account
            </button>
            <button onClick={toggleTheme} className="p-2 rounded-full hover:bg-[#352770] dark:hover:bg-[#352770]">
              {isDark ? '☀️' : '🌙'}
            </button>
            <div className="flex items-center space-x-3">
              {user?.picture && (
                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
              )}
              <span className={`font-medium ${isDark ? 'text-white' : 'text-purple-100'}`}>{user?.name}</span>
            </div>
            <button
              onClick={logout}
              className="px-4 py-2 text-red-500 hover:bg-red-500/10 dark:hover:bg-red-900/20 rounded-lg"
              data-testid="logout-btn"
            >
              Logout
            </button>
          </div>
        </div>
      </nav>
      <main className="max-w-7xl mx-auto px-4 py-8" data-testid="dashboard">
        {/* Quick Actions */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <button
            onClick={() => navigate('/create-options')}
            className="p-6 bg-gradient-to-r from-violet-500 to-purple-600 rounded-2xl text-white font-bold text-lg hover:scale-105 transition-transform"
            data-testid="create-quiz-card"
          >
            <span className="text-3xl block mb-2">✨</span>
            Create Quiz
          </button>
          <button
            onClick={() => navigate('/upload')}
            className="p-6 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl text-white font-bold text-lg hover:scale-105 transition-transform"
            data-testid="upload-pdf-card"
          >
            <span className="text-3xl block mb-2">📄</span>
            Upload PDF
          </button>
          <button
            onClick={() => navigate('/ai-generate')}
            className="p-6 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl text-white font-bold text-lg hover:scale-105 transition-transform"
            data-testid="ai-generate-card"
          >
            <span className="text-3xl block mb-2">🤖</span>
            AI from Topic
          </button>
          <button
            onClick={() => navigate('/browse')}
            className="p-6 bg-gradient-to-r from-teal-500 to-green-500 rounded-2xl text-white font-bold text-lg hover:scale-105 transition-transform"
            data-testid="browse-quizzes-card"
          >
            <span className="text-3xl block mb-2">🌐</span>
            Public Quizzes
          </button>
        </div>
        {/* My Quizzes */}
        <section className="mb-12">
          <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-white'}`}>My Quizzes</h2>
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500 mx-auto"></div>
            </div>
          ) : quizzes.length === 0 ? (
            <div className={`text-center py-16 px-6 ${isDark ? 'bg-[#241651]' : 'bg-[#241651]'} rounded-2xl shadow`}>
              <div className="text-6xl mb-4" aria-hidden="true">✨</div>
              <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-white'}`}>
                No quizzes yet
              </h3>
              <p className={`mb-6 ${isDark ? 'text-purple-300' : 'text-purple-300'}`}>
                Build a quiz from a topic, a PDF, or from scratch — it only takes a minute.
              </p>
              <button
                type="button"
                onClick={() => navigate('/create-options')}
                className="px-6 py-3 bg-gradient-to-r from-violet-500 to-purple-600 text-white font-semibold rounded-xl hover:scale-105 transition-transform"
                data-testid="dashboard-create-first-quiz"
              >
                Create your first quiz →
              </button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {quizzes.map((quiz) => (
                <div
                  key={quiz.quiz_id}
                  className={`${isDark ? 'bg-[#241651]' : 'bg-[#241651]'} rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow relative`}
                  data-testid={`quiz-card-${quiz.quiz_id}`}
                >
                  {/* Public/Private Badge */}
                  <span className={`absolute top-3 right-3 px-2 py-1 text-xs font-medium rounded-full ${
                    quiz.is_public 
                      ? 'bg-green-500/20 text-green-300 dark:bg-green-900/30 dark:text-green-400' 
                      : 'bg-[#352770] text-purple-200 dark:bg-[#352770] dark:text-purple-300'
                  }`}>
                    {quiz.is_public ? '🌐 Public' : '🔒 Private'}
                  </span>
                  <h3 className={`text-xl font-bold mb-2 pr-20 ${isDark ? 'text-white' : 'text-white'}`}>{quiz.title}</h3>
                  <p className={`text-sm mb-4 ${isDark ? 'text-purple-300' : 'text-purple-300'}`}>
                    {quiz.questions?.length || 0} questions • {quiz.play_count || 0} plays
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => navigate(`/host/${quiz.quiz_id}`)}
                      className="flex-1 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 text-sm"
                    >
                      Host Game
                    </button>
                    <button
                      onClick={() => navigate(`/edit/${quiz.quiz_id}`)}
                      className={`px-3 py-2 border rounded-lg text-sm ${isDark ? 'border-[#3b2580] hover:bg-[#352770]' : 'border-[#3b2580] hover:bg-[#352770]'}`}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => duplicateQuiz(quiz.quiz_id)}
                      className={`px-3 py-2 border rounded-lg text-sm ${isDark ? 'border-[#3b2580] hover:bg-[#352770]' : 'border-[#3b2580] hover:bg-[#352770]'}`}
                      title="Duplicate quiz"
                    >
                      📋
                    </button>
                    {quiz.is_public && (
                      <button
                        onClick={() => shareQuiz(quiz.quiz_id)}
                        className={`px-3 py-2 border rounded-lg text-sm ${copiedId === quiz.quiz_id ? 'bg-green-500 text-white border-green-500' : isDark ? 'border-[#3b2580] hover:bg-[#352770]' : 'border-[#3b2580] hover:bg-[#352770]'}`}
                        title="Copy share link"
                      >
                        {copiedId === quiz.quiz_id ? '✓' : '🔗'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};
// Browse Public Quizzes Component
const BrowseQuizzes = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    fetchPublicQuizzes();
  }, []);
  const fetchPublicQuizzes = async () => {
    try {
      const res = await fetch(`${API_URL}/api/quizzes/public`);
      const data = await res.json();
      setQuizzes(data.quizzes || []);
    } catch (error) {
      console.error('Failed to fetch quizzes:', error);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0f0529]' : 'bg-[#1a0d3e]'} py-8 px-4`}>
      <div className="max-w-6xl mx-auto">
        <button onClick={() => navigate('/dashboard')} className="text-purple-500 mb-6 hover:underline">
          ← Back to Dashboard
        </button>
        <h1 className={`text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-white'}`}>
          🌐 Public Quizzes
        </h1>
        <p className={`mb-8 ${isDark ? 'text-purple-300' : 'text-purple-300'}`}>
          Browse and host quizzes created by the community
        </p>
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500 mx-auto"></div>
          </div>
        ) : quizzes.length === 0 ? (
          <div className={`text-center py-12 ${isDark ? 'bg-[#241651]' : 'bg-[#241651]'} rounded-2xl`}>
            <p className={`text-lg ${isDark ? 'text-purple-300' : 'text-purple-300'}`}>No public quizzes available yet.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {quizzes.map((quiz) => (
              <div
                key={quiz.quiz_id}
                className={`${isDark ? 'bg-[#241651]' : 'bg-[#241651]'} rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow`}
              >
                <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-white'}`}>{quiz.title}</h3>
                <p className={`text-sm mb-1 ${isDark ? 'text-purple-300' : 'text-purple-300'}`}>
                  {quiz.questions?.length || 0} questions
                </p>
                <p className={`text-xs mb-4 ${isDark ? 'text-purple-300' : 'text-purple-300'}`}>
                  by {quiz.creator_name || 'Anonymous'} • {quiz.play_count || 0} plays
                </p>
                <button
                  onClick={() => navigate(`/host/${quiz.quiz_id}`)}
                  className="w-full py-2 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-lg hover:opacity-90"
                >
                  Host This Quiz
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
// Admin Dashboard Component
const AdminDashboard = () => {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const fetchStats = async () => {
    try {
      const res = await authFetch(`${API_URL}/api/admin/stats`);
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };
  if (!user?.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }
  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0f0529]' : 'bg-[#1a0d3e]'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }
  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0f0529]' : 'bg-[#1a0d3e]'} py-8 px-4`}>
      <div className="max-w-7xl mx-auto">
        <button onClick={() => navigate('/dashboard')} className="text-purple-500 mb-6 hover:underline">
          ← Back to Dashboard
        </button>
        <h1 className={`text-3xl font-bold mb-8 ${isDark ? 'text-white' : 'text-white'}`}>
          📊 Admin Dashboard
        </h1>
        {/* Overview Stats */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Users', value: stats?.overview?.total_users || 0, icon: '👥', color: 'from-blue-500 to-cyan-500' },
            { label: 'New Users (7d)', value: stats?.overview?.recent_users_7d || 0, icon: '📈', color: 'from-green-500 to-emerald-500' },
            { label: 'Total Quizzes', value: stats?.overview?.total_quizzes || 0, icon: '📝', color: 'from-violet-500 to-purple-600' },
            { label: 'Games Played', value: stats?.overview?.total_games || 0, icon: '🎮', color: 'from-orange-500 to-red-500' },
          ].map((stat) => (
            <div key={stat.label} className={`bg-gradient-to-r ${stat.color} rounded-2xl p-6 text-white`}>
              <div className="text-3xl mb-2">{stat.icon}</div>
              <div className="text-3xl font-bold">{stat.value}</div>
              <div className="text-sm opacity-80">{stat.label}</div>
            </div>
          ))}
        </div>
        {/* Quiz Stats */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <div className={`${isDark ? 'bg-[#241651]' : 'bg-[#241651]'} rounded-2xl p-6`}>
            <h3 className={`font-bold mb-2 ${isDark ? 'text-white' : 'text-white'}`}>Public Quizzes</h3>
            <p className="text-2xl font-bold text-green-500">{stats?.overview?.public_quizzes || 0}</p>
          </div>
          <div className={`${isDark ? 'bg-[#241651]' : 'bg-[#241651]'} rounded-2xl p-6`}>
            <h3 className={`font-bold mb-2 ${isDark ? 'text-white' : 'text-white'}`}>Private Quizzes</h3>
            <p className="text-2xl font-bold text-purple-300">{stats?.overview?.private_quizzes || 0}</p>
          </div>
          <div className={`${isDark ? 'bg-[#241651]' : 'bg-[#241651]'} rounded-2xl p-6`}>
            <h3 className={`font-bold mb-2 ${isDark ? 'text-white' : 'text-white'}`}>Active Games</h3>
            <p className="text-2xl font-bold text-purple-500">{stats?.overview?.active_games || 0}</p>
          </div>
        </div>
        {/* Top Quizzes */}
        <div className={`${isDark ? 'bg-[#241651]' : 'bg-[#241651]'} rounded-2xl p-6 mb-8`}>
          <h3 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-white'}`}>Top Quizzes by Plays</h3>
          <div className="space-y-3">
            {stats?.top_quizzes?.map((quiz, i) => (
              <div key={quiz.quiz_id} className={`flex items-center justify-between p-3 rounded-lg ${isDark ? 'bg-[#352770]' : 'bg-[#1a0d3e]'}`}>
                <div className="flex items-center space-x-3">
                  <span className="text-lg font-bold text-purple-500">#{i + 1}</span>
                  <div>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-white'}`}>{quiz.title}</p>
                    <p className={`text-xs ${isDark ? 'text-purple-300' : 'text-purple-300'}`}>
                      {quiz.is_public ? '🌐 Public' : '🔒 Private'}
                    </p>
                  </div>
                </div>
                <span className="font-bold text-green-500">{quiz.play_count} plays</span>
              </div>
            ))}
          </div>
        </div>
        {/* Recent Users */}
        <div className={`${isDark ? 'bg-[#241651]' : 'bg-[#241651]'} rounded-2xl p-6`}>
          <h3 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-white'}`}>Recent Users</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`text-left ${isDark ? 'text-purple-300' : 'text-purple-300'}`}>
                  <th className="pb-3">User</th>
                  <th className="pb-3">Email</th>
                  <th className="pb-3">Games Played</th>
                  <th className="pb-3">Total Score</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {stats?.users?.slice(0, 20).map((u) => (
                  <tr key={u.user_id}>
                    <td className={`py-3 ${isDark ? 'text-white' : 'text-white'}`}>
                      {u.name} {u.is_admin && <span className="text-xs bg-yellow-500 text-[#1a0d3e] px-2 py-0.5 rounded-full ml-2">Admin</span>}
                    </td>
                    <td className={`py-3 ${isDark ? 'text-purple-300' : 'text-purple-300'}`}>{u.email}</td>
                    <td className={`py-3 ${isDark ? 'text-purple-200' : 'text-purple-200'}`}>{u.stats?.games_played || 0}</td>
                    <td className={`py-3 font-medium text-purple-500`}>{u.stats?.total_score || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
// Join Game Component
const JoinGame = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [pin, setPin] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const handleJoin = async (e) => {
    e.preventDefault();
    if (!pin || !name) {
      setError('Please enter both PIN and name');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/games/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin, player_name: name })
      });
      const data = await res.json();
      if (res.ok) {
        // Bug #6 fix: store the server-issued player_token so
        // submitAnswer and the WebSocket can authenticate.
        localStorage.setItem('player_info', JSON.stringify({
          session_id: data.session_id,
          player_id: data.player_id,
          player_name: name,
          player_token: data.player_token
        }));
        navigate(`/lobby/${data.session_id}`);
      } else {
        setError(data.detail || 'Failed to join game');
      }
    } catch (error) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0f0529]' : 'bg-gradient-to-br from-[#1a0d3e] via-[#2d1b69] to-[#0f0529]'}`}>
      <div className={`${isDark ? 'bg-[#241651]' : 'bg-[#241651]'} rounded-3xl p-8 w-full max-w-md shadow-2xl`}>
        <button onClick={() => navigate('/')} className="text-purple-500 mb-6 hover:underline">
          ← Back
        </button>
        <h1 className={`text-3xl font-bold text-center mb-8 ${isDark ? 'text-white' : 'text-white'}`}>
          Join a Game
        </h1>
        <form onSubmit={handleJoin} className="space-y-6">
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-purple-200' : 'text-purple-100'}`}>
              Game PIN
            </label>
            <input
              type="text"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="Enter 6-digit PIN"
              className={`w-full px-4 py-4 text-2xl text-center font-bold rounded-xl border-2 ${
                isDark ? 'bg-[#352770] border-[#3b2580] text-white' : 'bg-[#1a0d3e] border-[#3b2580]'
              } focus:border-purple-500 focus:outline-none`}
              data-testid="pin-input"
              maxLength={6}
            />
          </div>
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-purple-200' : 'text-purple-100'}`}>
              Your Nickname
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 20))}
              placeholder="Enter your name"
              className={`w-full px-4 py-3 rounded-xl border-2 ${
                isDark ? 'bg-[#352770] border-[#3b2580] text-white' : 'bg-[#1a0d3e] border-[#3b2580]'
              } focus:border-purple-500 focus:outline-none`}
              data-testid="name-input"
            />
          </div>
          {error && <p className="text-red-500 text-center">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold text-xl rounded-xl hover:scale-105 transition-transform disabled:opacity-50"
            data-testid="join-btn"
          >
            {loading ? 'Joining...' : 'Join Game!'}
          </button>
        </form>
      </div>
    </div>
  );
};
// PDF Upload Component
const UploadPDF = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [difficulty, setDifficulty] = useState('medium');
  const [timePerQuestion, setTimePerQuestion] = useState(20);
  const [numQuestions, setNumQuestions] = useState(10);
  const [isPublic, setIsPublic] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState('');
  const [tierInfo, setTierInfo] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [checkingTier, setCheckingTier] = useState(true);
  useEffect(() => {
    // Check user's tier
    const checkTier = async () => {
      try {
        const res = await authFetch(`${API_URL}/api/account/tier`);
        if (res.ok) {
          const data = await res.json();
          setTierInfo(data);
          // If free tier, show upgrade modal immediately
          if (data.effective_tier === 'free') {
            setShowUpgradeModal(true);
          }
        }
      } catch (err) {
        console.error('Failed to check tier:', err);
      } finally {
        setCheckingTier(false);
      }
    };
    checkTier();
  }, []);
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file || !title) {
      setError('Please select a file and enter a title');
      return;
    }
    // Double-check tier before upload
    if (tierInfo?.effective_tier === 'free') {
      setShowUpgradeModal(true);
      return;
    }
    setLoading(true);
    setError('');
    setProgress('Uploading PDF...');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', title);
      formData.append('difficulty', difficulty);
      formData.append('time_per_question', timePerQuestion);
      formData.append('num_questions', numQuestions);
      formData.append('is_public', isPublic);
      setProgress('Analyzing PDF and generating quiz...');
      const res = await authFetch(`${API_URL}/api/quizzes/generate-from-pdf`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json();
      if (res.ok) {
        setProgress('Quiz created successfully!');
        setTimeout(() => navigate(`/host/${data.quiz_id}`), 1500);
      } else {
        if (res.status === 403) {
          setShowUpgradeModal(true);
        }
        setError(data.detail || 'Failed to generate quiz');
      }
    } catch (error) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  if (checkingTier) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0f0529]' : 'bg-gradient-to-br from-[#1a0d3e] via-[#2d1b69] to-[#0f0529]'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
      </div>
    );
  }
  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0f0529]' : 'bg-gradient-to-br from-[#1a0d3e] via-[#2d1b69] to-[#0f0529]'} py-12 px-4`}>
      <UpgradeModal 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)} 
        feature="pdf"
        currentTier={tierInfo?.tier_name}
      />
      <div className="max-w-2xl mx-auto">
        <button onClick={() => navigate('/dashboard')} className="text-white mb-6 hover:underline">
          ← Back to Dashboard
        </button>
        <div className={`${isDark ? 'bg-[#241651]' : 'bg-[#241651]'} rounded-3xl p-8 shadow-2xl`}>
          {/* Pro badge indicator */}
          {tierInfo?.effective_tier === 'free' && (
            <div className="mb-4 p-3 bg-[#352770] dark:bg-purple-900/30 rounded-xl text-center">
              <span className="text-purple-200 dark:text-purple-300 font-medium">
                🔒 PDF upload requires Pro subscription
              </span>
              <button 
                onClick={() => navigate('/pricing')}
                className="ml-2 text-purple-300 dark:text-purple-400 underline"
              >
                Upgrade now
              </button>
            </div>
          )}
          <h1 className={`text-3xl font-bold text-center mb-2 ${isDark ? 'text-white' : 'text-white'}`}>
            Upload PDF
          </h1>
          <p className={`text-center mb-8 ${isDark ? 'text-purple-300' : 'text-purple-300'}`}>
            Upload a PDF with quiz questions (auto-extracted up to 30), or any document to generate AI questions
          </p>
          <form onSubmit={handleUpload} className="space-y-6">
            {/* File Upload */}
            <div className={`border-2 border-dashed rounded-2xl p-8 text-center ${
              isDark ? 'border-[#3b2580] bg-[#352770]/50' : 'border-[#3b2580] bg-[#1a0d3e]'
            } ${tierInfo?.effective_tier === 'free' ? 'opacity-50' : ''}`}>
              <input
                type="file"
                accept=".pdf"
                onChange={(e) => setFile(e.target.files[0])}
                className="hidden"
                id="pdf-upload"
                data-testid="pdf-input"
                disabled={tierInfo?.effective_tier === 'free'}
              />
              <label htmlFor="pdf-upload" className={`${tierInfo?.effective_tier === 'free' ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                <div className="text-5xl mb-4">📄</div>
                <p className={`font-medium ${isDark ? 'text-purple-200' : 'text-purple-200'}`}>
                  {file ? file.name : 'Click to select PDF'}
                </p>
                <p className={`text-sm ${isDark ? 'text-purple-300' : 'text-purple-300'}`}>
                  Max 50MB
                </p>
              </label>
            </div>
            {/* Title */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-purple-200' : 'text-purple-100'}`}>
                Quiz Title
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter quiz title"
                className={`w-full px-4 py-3 rounded-xl border-2 ${
                  isDark ? 'bg-[#352770] border-[#3b2580] text-white' : 'bg-[#1a0d3e] border-[#3b2580]'
                } focus:border-purple-500 focus:outline-none`}
                data-testid="title-input"
              />
            </div>
            {/* Difficulty */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-purple-200' : 'text-purple-100'}`}>
                Difficulty (for AI-generated questions)
              </label>
              <select
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
                className={`w-full px-4 py-3 rounded-xl border-2 ${
                  isDark ? 'bg-[#352770] border-[#3b2580] text-white' : 'bg-[#1a0d3e] border-[#3b2580]'
                }`}
                data-testid="difficulty-select"
              >
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>
            {/* Number of Questions Slider (for AI generation) */}
            <div className={`p-4 rounded-xl ${isDark ? 'bg-purple-900/20 border border-purple-500/30' : 'bg-[#352770] border border-purple-200'}`}>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-purple-200' : 'text-purple-100'}`}>
                <span className="flex items-center gap-2">
                  🤖 AI Questions: <span className="text-purple-500 font-bold">{numQuestions}</span>
                </span>
              </label>
              <p className={`text-xs mb-3 ${isDark ? 'text-purple-300' : 'text-purple-300'}`}>
                If PDF doesn't contain quiz questions, AI will generate this many questions from the content
              </p>
              <input
                type="range"
                min="10"
                max="30"
                value={numQuestions}
                onChange={(e) => setNumQuestions(Number(e.target.value))}
                className="w-full h-2 bg-[#352770] dark:bg-[#352770] rounded-lg appearance-none cursor-pointer accent-purple-500"
                data-testid="num-questions-slider"
              />
              <div className="flex justify-between text-xs mt-1">
                <span className={isDark ? 'text-purple-300' : 'text-purple-300'}>10</span>
                <span className={isDark ? 'text-purple-300' : 'text-purple-300'}>20</span>
                <span className={isDark ? 'text-purple-300' : 'text-purple-300'}>30</span>
              </div>
            </div>
            {/* Time Per Question Slider */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-purple-200' : 'text-purple-100'}`}>
                Time per Question: <span className="text-purple-500 font-bold">{timePerQuestion} seconds</span>
              </label>
              <input
                type="range"
                min="5"
                max="45"
                value={timePerQuestion}
                onChange={(e) => setTimePerQuestion(Number(e.target.value))}
                className="w-full h-2 bg-[#352770] dark:bg-[#352770] rounded-lg appearance-none cursor-pointer accent-purple-500"
                data-testid="time-slider"
              />
              <div className="flex justify-between text-xs mt-1">
                <span className={isDark ? 'text-purple-300' : 'text-purple-300'}>5s</span>
                <span className={isDark ? 'text-purple-300' : 'text-purple-300'}>45s</span>
              </div>
            </div>
            {/* Public/Private Toggle */}
            <div className={`p-4 rounded-xl ${isDark ? 'bg-[#352770]/50' : 'bg-[#1a0d3e]'}`}>
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-white'}`}>
                    {isPublic ? '🌐 Public Quiz' : '🔒 Private Quiz'}
                  </span>
                  <p className={`text-xs ${isDark ? 'text-purple-300' : 'text-purple-300'}`}>
                    {isPublic ? 'Anyone can find and host this quiz' : 'Only you can host this quiz'}
                  </p>
                </div>
                <div 
                  onClick={() => setIsPublic(!isPublic)}
                  className={`relative w-14 h-8 rounded-full transition-colors ${isPublic ? 'bg-green-500' : 'bg-[#3b2580]'}`}
                >
                  <div className={`absolute top-1 w-6 h-6 bg-[#241651] rounded-full transition-transform ${isPublic ? 'translate-x-7' : 'translate-x-1'}`}></div>
                </div>
              </label>
            </div>
            {error && <p className="text-red-500 text-center">{error}</p>}
            {progress && !error && <p className="text-purple-500 text-center">{progress}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-gradient-to-r from-violet-500 to-purple-600 text-white font-bold text-xl rounded-xl hover:scale-105 transition-transform disabled:opacity-50"
              data-testid="generate-btn"
            >
              {loading ? 'Generating...' : 'Generate Quiz'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
// Create Quiz Component
const CreateQuiz = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [questions, setQuestions] = useState([
    { text: '', question_type: 'mcq', answers: [{ text: '', is_correct: true }, { text: '', is_correct: false }, { text: '', is_correct: false }, { text: '', is_correct: false }], time_limit: 20 }
  ]);
  const [timePerQuestion, setTimePerQuestion] = useState(20);
  const [isPublic, setIsPublic] = useState(false);
  const [saving, setSaving] = useState(false);
  const toggleQuestionType = (qIndex) => {
    const updated = [...questions];
    const q = updated[qIndex];
    if (q.question_type === 'tf') {
      q.question_type = 'mcq';
      q.answers = [{ text: '', is_correct: true }, { text: '', is_correct: false }, { text: '', is_correct: false }, { text: '', is_correct: false }];
    } else {
      q.question_type = 'tf';
      q.answers = [{ text: 'True', is_correct: true }, { text: 'False', is_correct: false }];
    }
    setQuestions(updated);
  };
  const addQuestion = () => {
    setQuestions([...questions, {
      text: '', question_type: 'mcq',
      answers: [{ text: '', is_correct: true }, { text: '', is_correct: false }, { text: '', is_correct: false }, { text: '', is_correct: false }],
      time_limit: timePerQuestion
    }]);
  };
  const updateQuestion = (index, field, value) => {
    const updated = [...questions];
    updated[index][field] = value;
    setQuestions(updated);
  };
  const updateAnswer = (qIndex, aIndex, field, value) => {
    const updated = [...questions];
    if (field === 'is_correct' && value) {
      updated[qIndex].answers.forEach((a, i) => a.is_correct = i === aIndex);
    } else {
      updated[qIndex].answers[aIndex][field] = value;
    }
    setQuestions(updated);
  };
  // Update all questions when time slider changes
  const handleTimeChange = (newTime) => {
    setTimePerQuestion(newTime);
    setQuestions(questions.map(q => ({ ...q, time_limit: newTime })));
  };
  const handleSave = async () => {
    if (!title) return;
    setSaving(true);
    try {
      const res = await authFetch(`${API_URL}/api/quizzes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, questions, is_public: isPublic })
      });
      const data = await res.json();
      if (res.ok) {
        navigate(`/host/${data.quiz_id}`);
      }
    } catch (error) {
      console.error('Failed to save quiz:', error);
    } finally {
      setSaving(false);
    }
  };
  const colors = ['bg-quiz-red', 'bg-quiz-blue', 'bg-quiz-yellow', 'bg-quiz-green'];
  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0f0529]' : 'bg-[#1a0d3e]'} py-8 px-4`}>
      <div className="max-w-4xl mx-auto">
        <button onClick={() => navigate('/dashboard')} className="text-purple-500 mb-6 hover:underline">
          ← Back to Dashboard
        </button>
        <div className={`${isDark ? 'bg-[#241651]' : 'bg-[#241651]'} rounded-3xl p-8 shadow-lg mb-8`}>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Quiz Title"
            className={`w-full text-3xl font-bold mb-4 bg-transparent border-b-2 ${
              isDark ? 'text-white border-[#3b2580]' : 'text-white border-[#3b2580]'
            } focus:border-purple-500 focus:outline-none pb-2`}
            data-testid="quiz-title-input"
          />
          {/* Time Per Question Slider */}
          <div className="mt-4">
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-purple-200' : 'text-purple-100'}`}>
              Time per Question: <span className="text-purple-500 font-bold">{timePerQuestion} seconds</span>
            </label>
            <input
              type="range"
              min="5"
              max="45"
              value={timePerQuestion}
              onChange={(e) => handleTimeChange(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
              data-testid="time-slider"
            />
            <div className="flex justify-between text-xs mt-1">
              <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>5s</span>
              <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>45s</span>
            </div>
          </div>
          {/* Public/Private Toggle */}
          <div className="mt-6">
            <label className={`flex items-center justify-between p-4 rounded-xl ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
              <div>
                <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  {isPublic ? '🌐 Public Quiz' : '🔒 Private Quiz'}
                </span>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {isPublic ? 'Anyone can find and host this quiz' : 'Only you can host this quiz'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsPublic(!isPublic)}
                className={`relative w-14 h-8 rounded-full transition-colors ${isPublic ? 'bg-green-500' : 'bg-gray-400'}`}
                data-testid="public-toggle"
              >
                <span className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-transform ${isPublic ? 'translate-x-7' : 'translate-x-1'}`} />
              </button>
            </label>
          </div>
        </div>
        {questions.map((q, qIndex) => (
          <div key={`q-${qIndex}`} className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-3xl p-8 shadow-lg mb-6`}>
            <div className="flex items-center justify-between mb-4">
              <span className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Question {qIndex + 1}
              </span>
              {questions.length > 1 && (
                <button
                  onClick={() => setQuestions(questions.filter((_, i) => i !== qIndex))}
                  className="text-red-500 hover:text-red-700"
                >
                  Remove
                </button>
              )}
            </div>
            <textarea
              value={q.text}
              onChange={(e) => updateQuestion(qIndex, 'text', e.target.value)}
              placeholder="Enter your question"
              className={`w-full px-4 py-3 rounded-xl border-2 mb-6 ${
                isDark ? 'bg-[#352770] border-[#3b2580] text-white' : 'bg-[#1a0d3e] border-[#3b2580]'
              } focus:border-purple-500 focus:outline-none resize-none`}
              rows={2}
              data-testid={`question-${qIndex}-input`}
            />
            <div className="grid grid-cols-2 gap-4">
              {q.answers.map((a, aIndex) => (
                <div
                  key={`q${qIndex}-a${aIndex}`}
                  className={`${colors[aIndex]} rounded-xl p-4 ${a.is_correct ? 'ring-4 ring-white' : ''}`}
                >
                  <input
                    type="text"
                    value={a.text}
                    onChange={(e) => updateAnswer(qIndex, aIndex, 'text', e.target.value)}
                    placeholder={`Answer ${aIndex + 1}`}
                    className="w-full bg-transparent text-white placeholder-white/60 font-medium focus:outline-none"
                    data-testid={`answer-${qIndex}-${aIndex}-input`}
                  />
                  <label className="flex items-center mt-2 text-white/80 text-sm">
                    <input
                      type="radio"
                      checked={a.is_correct}
                      onChange={() => updateAnswer(qIndex, aIndex, 'is_correct', true)}
                      className="mr-2"
                    />
                    Correct answer
                  </label>
                </div>
              ))}
            </div>
          </div>
        ))}
        <div className="flex space-x-4">
          <button
            onClick={addQuestion}
            className={`flex-1 py-4 border-2 border-dashed rounded-xl font-medium ${
              isDark ? 'border-[#3b2580] text-purple-300 hover:border-[#7c3aed]' : 'border-[#3b2580] text-purple-300 hover:border-[#7c3aed]'
            }`}
            data-testid="add-question-btn"
          >
            + Add Question
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !title}
            className="flex-1 py-4 bg-gradient-to-r from-violet-500 to-purple-600 text-white font-bold rounded-xl hover:scale-105 transition-transform disabled:opacity-50"
            data-testid="save-quiz-btn"
          >
            {saving ? 'Saving...' : 'Save & Host'}
          </button>
        </div>
      </div>
    </div>
  );
};
// Edit Quiz Component
const EditQuiz = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const { quizId } = useParams();
  const [title, setTitle] = useState('');
  const [questions, setQuestions] = useState([]);
  const [timePerQuestion, setTimePerQuestion] = useState(20);
  const [isPublic, setIsPublic] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useEffect(() => {
    const fetchQuiz = async () => {
      try {
        const res = await authFetch(`${API_URL}/api/quizzes/${quizId}`);
        if (res.ok) {
          const data = await res.json();
          setTitle(data.title || '');
          setIsPublic(data.is_public || false);
          setQuestions(data.questions?.map(q => ({
            text: q.text || '',
            answers: q.answers || [{ text: '', is_correct: true }, { text: '', is_correct: false }, { text: '', is_correct: false }, { text: '', is_correct: false }],
            time_limit: q.time_limit || 20,
            explanation: q.explanation || '',
            image_url: q.image_url || ''
          })) || []);
          if (data.questions?.length > 0) {
            setTimePerQuestion(data.questions[0].time_limit || 20);
          }
        } else {
          setError('Quiz not found');
        }
      } catch (err) {
        setError('Failed to load quiz');
      } finally {
        setLoading(false);
      }
    };
    fetchQuiz();
  }, [quizId]);
  const addQuestion = () => {
    setQuestions([...questions, {
      text: '', answers: [{ text: '', is_correct: true }, { text: '', is_correct: false }, { text: '', is_correct: false }, { text: '', is_correct: false }], time_limit: timePerQuestion
    }]);
  };
  const removeQuestion = (index) => {
    if (questions.length <= 1) return;
    setQuestions(questions.filter((_, i) => i !== index));
  };
  const updateQuestion = (index, field, value) => {
    const updated = [...questions];
    updated[index][field] = value;
    setQuestions(updated);
  };
  const updateAnswer = (qIndex, aIndex, field, value) => {
    const updated = [...questions];
    if (field === 'is_correct' && value) {
      updated[qIndex].answers.forEach((a, i) => a.is_correct = i === aIndex);
    } else {
      updated[qIndex].answers[aIndex][field] = value;
    }
    setQuestions(updated);
  };
  const handleTimeChange = (newTime) => {
    setTimePerQuestion(newTime);
    setQuestions(questions.map(q => ({ ...q, time_limit: newTime })));
  };
  const handleSave = async () => {
    if (!title) return;
    setSaving(true);
    try {
      const res = await authFetch(`${API_URL}/api/quizzes/${quizId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, questions, is_public: isPublic })
      });
      if (res.ok) {
        navigate('/dashboard');
      } else {
        const data = await res.json();
        setError(data.detail || 'Failed to save');
      }
    } catch (err) {
      setError('Failed to save quiz');
    } finally {
      setSaving(false);
    }
  };
  const colors = ['bg-quiz-red', 'bg-quiz-blue', 'bg-quiz-yellow', 'bg-quiz-green'];
  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0f0529]' : 'bg-[#1a0d3e]'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }
  if (error && !title) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0f0529]' : 'bg-[#1a0d3e]'}`}>
        <div className="text-center">
          <p className="text-red-500 text-lg mb-4">{error}</p>
          <button onClick={() => navigate('/dashboard')} className="text-purple-500 hover:underline">Back to Dashboard</button>
        </div>
      </div>
    );
  }
  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'} py-8 px-4`}>
      <div className="max-w-4xl mx-auto">
        <button onClick={() => navigate('/dashboard')} className="text-purple-500 mb-6 hover:underline" data-testid="edit-back-btn">
          ← Back to Dashboard
        </button>
        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 shadow-lg mb-6`}>
          <h1 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-testid="edit-quiz-title">Edit Quiz</h1>
          {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Quiz Title"
            className={`w-full px-4 py-3 rounded-xl border-2 mb-4 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-200'} focus:border-purple-500 focus:outline-none`}
            data-testid="edit-title-input"
          />
          <div className="flex items-center gap-4 mb-4">
            <label className={`flex items-center gap-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} className="accent-purple-500" />
              Public Quiz
            </label>
            <div className={`flex items-center gap-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              <span className="text-sm">Time: {timePerQuestion}s</span>
              <input type="range" min={5} max={45} value={timePerQuestion} onChange={(e) => handleTimeChange(Number(e.target.value))} className="accent-purple-500" />
            </div>
          </div>
        </div>
        {questions.map((q, qIndex) => (
          <div key={`eq-${qIndex}`} className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 shadow-lg mb-4`} data-testid={`edit-question-${qIndex}`}>
            <div className="flex justify-between items-center mb-3">
              <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Question {qIndex + 1}</h3>
              {questions.length > 1 && (
                <button onClick={() => removeQuestion(qIndex)} className="text-red-500 text-sm hover:underline" data-testid={`remove-q-${qIndex}`}>Remove</button>
              )}
            </div>
            <input
              type="text"
              value={q.text}
              onChange={(e) => updateQuestion(qIndex, 'text', e.target.value)}
              placeholder="Enter question"
              className={`w-full px-4 py-3 rounded-xl border-2 mb-4 ${isDark ? 'bg-[#352770] border-[#3b2580] text-white' : 'bg-[#1a0d3e] border-[#3b2580]'} focus:border-purple-500 focus:outline-none`}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {q.answers.map((a, aIndex) => (
                <div key={`eq${qIndex}-a${aIndex}`} className={`${colors[aIndex]} rounded-xl p-3`}>
                  <input
                    type="text"
                    value={a.text}
                    onChange={(e) => updateAnswer(qIndex, aIndex, 'text', e.target.value)}
                    placeholder={`Answer ${aIndex + 1}`}
                    className="w-full bg-white/20 text-white placeholder-white/60 px-3 py-2 rounded-lg focus:outline-none"
                  />
                  <label className="flex items-center mt-2 text-white text-sm">
                    <input type="radio" name={`correct-${qIndex}`} checked={a.is_correct} onChange={() => updateAnswer(qIndex, aIndex, 'is_correct', true)} className="mr-2" />
                    Correct answer
                  </label>
                </div>
              ))}
            </div>
          </div>
        ))}
        <div className="flex space-x-4">
          <button onClick={addQuestion} className={`flex-1 py-4 border-2 border-dashed rounded-xl font-medium ${isDark ? 'border-[#3b2580] text-purple-300 hover:border-[#7c3aed]' : 'border-[#3b2580] text-purple-300 hover:border-[#7c3aed]'}`} data-testid="edit-add-question-btn">
            + Add Question
          </button>
          <button onClick={handleSave} disabled={saving || !title} className="flex-1 py-4 bg-gradient-to-r from-violet-500 to-purple-600 text-white font-bold rounded-xl hover:scale-105 transition-transform disabled:opacity-50" data-testid="edit-save-btn">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};
// AI Generate from Topic Component
const AIGenerateQuiz = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [topic, setTopic] = useState('');
  const [difficulty, setDifficulty] = useState('medium');
  const [timePerQuestion, setTimePerQuestion] = useState(20);
  const [isPublic, setIsPublic] = useState(false);
  const [generateImages, setGenerateImages] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tierInfo, setTierInfo] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  useEffect(() => {
    // Check user's tier and credits
    const checkTier = async () => {
      try {
        const res = await authFetch(`${API_URL}/api/account/tier`);
        if (res.ok) {
          const data = await res.json();
          setTierInfo(data);
        }
      } catch (err) {
        console.error('Failed to check tier:', err);
      }
    };
    checkTier();
  }, []);
  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!topic.trim()) {
      setError('Please enter a topic');
      return;
    }
    // Check credits before generating
    if (tierInfo?.effective_tier === 'free' && tierInfo?.credits?.ai_topic <= 0) {
      setShowUpgradeModal(true);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await authFetch(`${API_URL}/api/quizzes/generate-from-topic`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          topic: topic.trim(), 
          difficulty, 
          time_per_question: timePerQuestion,
          is_public: isPublic,
          generate_images: generateImages
        })
      });
      const data = await res.json();
      if (res.ok) {
        // Update credits in local state
        if (data.remaining_credits !== null && tierInfo) {
          setTierInfo(prev => ({
            ...prev,
            credits: { ...prev.credits, ai_topic: data.remaining_credits }
          }));
        }
        navigate(`/host/${data.quiz_id}`);
      } else {
        if (res.status === 403) {
          setShowUpgradeModal(true);
        }
        setError(data.detail || 'Failed to generate quiz');
      }
    } catch (error) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  const creditsRemaining = tierInfo?.limits?.ai_topic_unlimited ? '∞' : tierInfo?.credits?.ai_topic;
  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0f0529]' : 'bg-gradient-to-br from-[#1a0d3e] via-[#2d1b69] to-[#0f0529]'} py-12 px-4`}>
      <UpgradeModal 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)} 
        feature="ai_credits"
        currentTier={tierInfo?.tier_name}
      />
      <div className="max-w-2xl mx-auto">
        <button onClick={() => navigate('/dashboard')} className="text-white mb-6 hover:underline">
          ← Back to Dashboard
        </button>
        <div className={`${isDark ? 'bg-[#241651]' : 'bg-[#241651]'} rounded-3xl p-8 shadow-2xl`}>
          {/* Credits indicator */}
          {tierInfo && (
            <div className={`mb-4 p-3 rounded-xl text-center ${
              tierInfo.limits?.ai_topic_unlimited 
                ? 'bg-green-500/20 dark:bg-green-900/30' 
                : tierInfo.credits?.ai_topic > 0 
                  ? 'bg-blue-500/20 dark:bg-blue-900/30'
                  : 'bg-red-500/20 dark:bg-red-900/30'
            }`}>
              <span className={`font-medium ${
                tierInfo.limits?.ai_topic_unlimited 
                  ? 'text-green-300 dark:text-green-300'
                  : tierInfo.credits?.ai_topic > 0 
                    ? 'text-blue-300 dark:text-blue-300'
                    : 'text-red-300 dark:text-red-300'
              }`}>
                {tierInfo.limits?.ai_topic_unlimited ? (
                  '✨ Unlimited AI Quizzes (Pro)'
                ) : tierInfo.credits?.ai_topic > 0 ? (
                  `🎯 ${creditsRemaining} AI quiz credit${tierInfo.credits?.ai_topic !== 1 ? 's' : ''} remaining this month`
                ) : (
                  <>
                    ❌ No credits remaining
                    <button onClick={() => navigate('/pricing')} className="ml-2 underline">
                      Upgrade to Pro
                    </button>
                  </>
                )}
              </span>
            </div>
          )}
          <h1 className={`text-3xl font-bold text-center mb-2 ${isDark ? 'text-white' : 'text-white'}`}>
            AI Quiz Generator
          </h1>
          <p className={`text-center mb-8 ${isDark ? 'text-purple-300' : 'text-purple-300'}`}>
            Enter any topic and AI will create a 10-question quiz
          </p>
          <form onSubmit={handleGenerate} className="space-y-6">
            {/* Topic Input */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-purple-200' : 'text-purple-100'}`}>
                Quiz Topic
              </label>
              <input
                type="text"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g., World War II, Python Programming, Solar System..."
                className={`w-full px-4 py-4 text-lg rounded-xl border-2 ${
                  isDark ? 'bg-[#352770] border-[#3b2580] text-white' : 'bg-[#1a0d3e] border-[#3b2580]'
                } focus:border-orange-500 focus:outline-none`}
                data-testid="topic-input"
              />
            </div>
            {/* Difficulty */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-purple-200' : 'text-purple-100'}`}>
                Difficulty Level
              </label>
              <div className="grid grid-cols-3 gap-3">
                {['easy', 'medium', 'hard'].map((level) => (
                  <button
                    key={level}
                    type="button"
                    onClick={() => setDifficulty(level)}
                    className={`py-3 rounded-xl font-medium capitalize transition-all ${
                      difficulty === level
                        ? 'bg-orange-500 text-white'
                        : isDark
                        ? 'bg-[#352770] text-purple-200 hover:bg-[#352770]'
                        : 'bg-[#352770] text-purple-200 hover:bg-[#352770]'
                    }`}
                    data-testid={`difficulty-${level}-btn`}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </div>
            {/* Time Per Question Slider */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-purple-200' : 'text-purple-100'}`}>
                Time per Question: <span className="text-orange-500 font-bold">{timePerQuestion} seconds</span>
              </label>
              <input
                type="range"
                min="5"
                max="45"
                value={timePerQuestion}
                onChange={(e) => setTimePerQuestion(Number(e.target.value))}
                className="w-full h-2 bg-[#352770] dark:bg-[#352770] rounded-lg appearance-none cursor-pointer accent-orange-500"
                data-testid="time-slider"
              />
              <div className="flex justify-between text-xs mt-1">
                <span className={isDark ? 'text-purple-300' : 'text-purple-300'}>5s</span>
                <span className={isDark ? 'text-purple-300' : 'text-purple-300'}>45s</span>
              </div>
            </div>
            {/* Public/Private Toggle */}
            <div className={`p-4 rounded-xl ${isDark ? 'bg-[#352770]/50' : 'bg-[#1a0d3e]'}`}>
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-white'}`}>
                    {isPublic ? '🌐 Public Quiz' : '🔒 Private Quiz'}
                  </span>
                  <p className={`text-xs ${isDark ? 'text-purple-300' : 'text-purple-300'}`}>
                    {isPublic ? 'Anyone can find and host this quiz' : 'Only you can host this quiz'}
                  </p>
                </div>
                <div 
                  onClick={() => setIsPublic(!isPublic)}
                  className={`relative w-14 h-8 rounded-full transition-colors ${isPublic ? 'bg-green-500' : 'bg-[#3b2580]'}`}
                >
                  <div className={`absolute top-1 w-6 h-6 bg-[#241651] rounded-full transition-transform ${isPublic ? 'translate-x-7' : 'translate-x-1'}`}></div>
                </div>
              </label>
            </div>
            {/* Generate Images Toggle */}
            <div className={`p-4 rounded-xl ${isDark ? 'bg-[#352770]/50' : 'bg-[#1a0d3e]'}`}>
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-white'}`}>
                    🖼️ Generate Images
                  </span>
                  <p className={`text-xs ${isDark ? 'text-purple-300' : 'text-purple-300'}`}>
                    Add relevant GIFs/images to make quiz more engaging
                  </p>
                </div>
                <div 
                  onClick={() => setGenerateImages(!generateImages)}
                  className={`relative w-14 h-8 rounded-full transition-colors ${generateImages ? 'bg-purple-500' : 'bg-[#3b2580]'}`}
                >
                  <div className={`absolute top-1 w-6 h-6 bg-[#241651] rounded-full transition-transform ${generateImages ? 'translate-x-7' : 'translate-x-1'}`}></div>
                </div>
              </label>
            </div>
            {error && <p className="text-red-500 text-center">{error}</p>}
            <button
              type="submit"
              disabled={loading || !topic.trim()}
              className="w-full py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold text-xl rounded-xl hover:scale-105 transition-transform disabled:opacity-50 disabled:hover:scale-100"
              data-testid="generate-topic-btn"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {generateImages ? 'Generating Quiz & Images...' : 'Generating Quiz...'}
                </span>
              ) : (
                'Generate Quiz 🚀'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
// Host Game / Lobby Component
const HostGame = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const { quizId } = useParams();

  const [game, setGame] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showExistingGamePrompt, setShowExistingGamePrompt] = useState(false);
  const [existingGameInfo, setExistingGameInfo] = useState(null);
  const [shuffleQuestions, setShuffleQuestions] = useState(false);
  const [teamMode, setTeamMode] = useState(false);
  const [numTeams, setNumTeams] = useState(2);
  const gameRef = React.useRef(null);
  const pollRef = React.useRef(null);
  const wsRef = React.useRef(null);

  useEffect(() => {
    createGame(false);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizId]);

  const createGame = async (forceNew = false) => {
    try {
      setLoading(true);
      const res = await authFetch(`${API_URL}/api/games/create?quiz_id=${quizId}&force_new=${forceNew}`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        // Check if this is an existing game with players
        if (data.rejoined && data.existing_players > 0) {
          setExistingGameInfo(data);
          setShowExistingGamePrompt(true);
          setLoading(false);
          return;
        }
        
        setShowExistingGamePrompt(false);
        setGame(data);
        gameRef.current = data;
        const quizRes = await fetch(`${API_URL}/api/quizzes/${quizId}`);
        if (quizRes.ok) setQuiz(await quizRes.json());
        // Always-on polling (never stops)
        pollRef.current = setInterval(async () => {
          try {
            const r = await fetch(`${API_URL}/api/games/${data.session_id}/state`);
            if (r.ok) { const d = await r.json(); setPlayers(d.players || []); }
          } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Polling error:', e); }
        }, 2000);
        // Also try WebSocket for instant updates
        connectWs(data.session_id);
      }
    } catch (error) {
      console.error('Failed to create game:', error);
    } finally {
      setLoading(false);
    }
  };

  const continueExistingGame = async () => {
    setShowExistingGamePrompt(false);
    setGame(existingGameInfo);
    gameRef.current = existingGameInfo;
    const quizRes = await fetch(`${API_URL}/api/quizzes/${quizId}`);
    if (quizRes.ok) setQuiz(await quizRes.json());
    // Start polling and WebSocket
    pollRef.current = setInterval(async () => {
      try {
        const r = await fetch(`${API_URL}/api/games/${existingGameInfo.session_id}/state`);
        if (r.ok) { const d = await r.json(); setPlayers(d.players || []); }
      } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Poll error:', e); }
    }, 2000);
    connectWs(existingGameInfo.session_id);
  };

  const resetGame = async () => {
    if (!game) return;
    try {
      await authFetch(`${API_URL}/api/games/${game.session_id}/reset`, { method: 'POST' });
      setPlayers([]);
    } catch (error) {
      console.error('Failed to reset game:', error);
    }
  };

  const connectWs = (sessionId) => {
    if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
    try {
      const socket = new WebSocket(`${API_URL.replace('http', 'ws')}/api/ws/${sessionId}/host`);
      socket.onmessage = (event) => {
        const msg = JSON.parse(event.data);
        if (msg.type === 'connected') setPlayers(msg.players || []);
        else if (msg.type === 'player_joined') setPlayers(prev => [...prev.filter(p => p.player_id !== msg.player.player_id), msg.player]);
        else if (msg.type === 'player_left') setPlayers(prev => prev.filter(p => p.player_id !== msg.player_id));
      };
      socket.onclose = () => {
        wsRef.current = null;
        setTimeout(() => { if (gameRef.current) connectWs(gameRef.current.session_id); }, 3000);
      };
      // Keepalive ping every 15s
      const ping = setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) {
          try { socket.send(JSON.stringify({ type: 'ping' })); } catch (e) { /* ping failed, socket closing */ }
        } else clearInterval(ping);
      }, 15000);
      socket.addEventListener('close', () => clearInterval(ping));
      wsRef.current = socket;
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('WebSocket connect error:', e); }
  };

  const startGame = async () => {
    if (!game) return;
    // Bug #2 fix: authFetch so the session cookie/header is attached.
    const res = await authFetch(`${API_URL}/api/games/${game.session_id}/start`, { method: 'POST' });
    if (!res.ok) {
      console.error('Failed to start game', res.status);
      return;
    }
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
    navigate(`/play/${game.session_id}?host=true`);
  };
  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0f0529]' : 'bg-purple-600'}`}>
        <div className="text-white text-xl">Creating game...</div>
      </div>
    );
  }
  
  // Show prompt when existing game has players
  if (showExistingGamePrompt && existingGameInfo) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-[#0f0529]' : 'bg-gradient-to-br from-[#1a0d3e] via-[#2d1b69] to-[#0f0529]'} py-12 px-4`}>
        <div className="max-w-lg mx-auto">
          <div className={`${isDark ? 'bg-[#241651]' : 'bg-[#241651]'} rounded-3xl p-8 shadow-2xl text-center`}>
            <h1 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-white'}`}>
              ⚠️ Existing Game Found
            </h1>
            <p className={`mb-6 ${isDark ? 'text-purple-300' : 'text-purple-200'}`}>
              You have an existing game session with <span className="font-bold text-purple-500">{existingGameInfo.existing_players} players</span> from a previous session.
            </p>
            <p className={`mb-8 text-sm ${isDark ? 'text-purple-300' : 'text-purple-300'}`}>
              Game PIN: <span className="font-bold">{existingGameInfo.pin}</span>
            </p>
            <div className="space-y-4">
              <button
                onClick={continueExistingGame}
                className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-bold rounded-xl hover:scale-105 transition-transform"
                data-testid="continue-existing-btn"
              >
                📋 Continue with Existing Players
              </button>
              <button
                onClick={() => createGame(true)}
                className="w-full px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold rounded-xl hover:scale-105 transition-transform"
                data-testid="start-fresh-btn"
              >
                🆕 Start Fresh Game (New PIN)
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0f0529]' : 'bg-gradient-to-br from-[#1a0d3e] via-[#2d1b69] to-[#0f0529]'} py-12 px-4`}>
      <div className="max-w-4xl mx-auto">
        <div className={`${isDark ? 'bg-[#241651]' : 'bg-[#241651]'} rounded-3xl p-8 shadow-2xl text-center`}>
          <h1 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-white'}`}>
            {quiz?.title}
          </h1>
          <p className={`mb-8 ${isDark ? 'text-purple-300' : 'text-purple-300'}`}>
            Share this PIN with players to join!
          </p>
          <div className="mb-8">
            <p className={`text-sm mb-2 ${isDark ? 'text-purple-300' : 'text-purple-300'}`}>Game PIN</p>
            <div className="text-6xl font-bold text-purple-300 tracking-widest" data-testid="game-pin">
              {game?.pin}
            </div>
          </div>
          {/* Game Settings */}
          <div className={`mb-6 grid grid-cols-1 md:grid-cols-3 gap-3 text-left ${isDark ? 'text-purple-200' : 'text-purple-200'}`}>
            <label className={`flex items-center justify-between p-3 rounded-xl ${isDark ? 'bg-[#352770]' : 'bg-[#352770]'} cursor-pointer`}>
              <span className="text-sm font-medium">🔀 Shuffle Questions</span>
              <div onClick={() => setShuffleQuestions(!shuffleQuestions)} className={`relative w-12 h-6 rounded-full transition-colors ${shuffleQuestions ? 'bg-purple-500' : 'bg-[#3b2580]'}`}>
                <div className={`absolute top-0.5 w-5 h-5 bg-[#241651] rounded-full transition-transform ${shuffleQuestions ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </div>
            </label>
            <label className={`flex items-center justify-between p-3 rounded-xl ${isDark ? 'bg-[#352770]' : 'bg-[#352770]'} cursor-pointer`}>
              <span className="text-sm font-medium">👥 Team Mode</span>
              <div onClick={() => setTeamMode(!teamMode)} className={`relative w-12 h-6 rounded-full transition-colors ${teamMode ? 'bg-purple-500' : 'bg-[#3b2580]'}`}>
                <div className={`absolute top-0.5 w-5 h-5 bg-[#241651] rounded-full transition-transform ${teamMode ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </div>
            </label>
            {teamMode && (
              <div className={`flex items-center justify-between p-3 rounded-xl ${isDark ? 'bg-[#352770]' : 'bg-[#352770]'}`}>
                <span className="text-sm font-medium">Teams:</span>
                <select value={numTeams} onChange={e => setNumTeams(Number(e.target.value))} className={`rounded-lg px-2 py-1 text-sm ${isDark ? 'bg-[#352770] text-white' : 'bg-[#241651]'}`}>
                  {[2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n} Teams</option>)}
                </select>
              </div>
            )}
            <div className="flex items-center justify-center">
              <MuteButton />
            </div>
          </div>
          <div className="mb-8">
            <h2 className={`text-lg font-medium mb-4 ${isDark ? 'text-purple-200' : 'text-purple-100'}`}>
              Players ({players.length})
            </h2>
            <div className="flex flex-wrap justify-center gap-3">
              {players.map((player, i) => (
                <div
                  key={player.player_id}
                  className={`px-4 py-2 rounded-full font-medium text-white flex items-center gap-2`}
                  style={{ backgroundColor: player.avatar_color || ['#E21B3C', '#1368CE', '#26890C', '#D89E00'][i % 4] }}
                >
                  <span>{player.avatar_emoji || '🎮'}</span>
                  {player.name}
                  {teamMode && player.team !== undefined && (
                    <span className="text-xs opacity-80">T{player.team + 1}</span>
                  )}
                </div>
              ))}
              {players.length === 0 && (
                <p className={isDark ? 'text-purple-300' : 'text-purple-300'}>Waiting for players...</p>
              )}
            </div>
          </div>
          <button
            onClick={startGame}
            disabled={players.length === 0}
            className="px-12 py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold text-xl rounded-xl hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="start-game-btn"
          >
            Start Game
          </button>
          {/* Host Controls */}
          <div className="mt-6 pt-6 border-t border-[#3b2580] dark:border-[#352770]">
            <p className={`text-sm mb-3 ${isDark ? 'text-purple-300' : 'text-purple-300'}`}>Host Options</p>
            <div className="flex justify-center gap-3">
              {players.length > 0 && (
                <button
                  onClick={resetGame}
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors"
                  data-testid="clear-players-btn"
                >
                  🗑️ Clear Players
                </button>
              )}
              <button
                onClick={() => createGame(true)}
                className="px-4 py-2 bg-[#352770] hover:bg-[#3b2580] text-white text-sm font-medium rounded-lg transition-colors"
                data-testid="new-game-btn"
              >
                🆕 New Game (New PIN)
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
// Game Lobby for Players
const GameLobby = () => {
  const { isDark } = useTheme();
  const { playCorrect, playWrong, playCountdownBeep, playCountdownGo, playStreak, playVictory, playTick } = useSound();
  const navigate = useNavigate();
  const sessionId = window.location.pathname.split('/lobby/')[1];
  const [players, setPlayers] = useState([]);
  const playerInfo = JSON.parse(localStorage.getItem('player_info') || '{}');
  const pollRef = React.useRef(null);
  const wsRef = React.useRef(null);

  useEffect(() => {
    if (!playerInfo.player_id) { navigate('/join'); return; }

    // Always-on polling: check game state every 1.5s
    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/api/games/${sessionId}/state`);
        if (res.ok) {
          const data = await res.json();
          setPlayers(data.players || []);
          if (data.status === 'active') {
            if (pollRef.current) clearInterval(pollRef.current);
            if (wsRef.current) wsRef.current.close();
            navigate(`/play/${sessionId}`);
          }
        }
      } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Lobby poll:', e); }
    };
    poll(); // Immediate first check
    pollRef.current = setInterval(poll, 1500);

    // Also try WebSocket for instant game_started signal
    try {
      const socket = new WebSocket(`${API_URL.replace('http', 'ws')}/api/ws/${sessionId}/${playerInfo.player_id}`);
      socket.onmessage = (event) => {
        const msg = JSON.parse(event.data);
        if (msg.type === 'connected') {
          setPlayers(msg.players || []);
          if (msg.game_status === 'active') {
            if (pollRef.current) clearInterval(pollRef.current);
            navigate(`/play/${sessionId}`);
          }
        } else if (msg.type === 'player_joined') {
          setPlayers(prev => [...prev.filter(p => p.player_id !== msg.player.player_id), msg.player]);
        } else if (msg.type === 'game_started') {
          if (pollRef.current) clearInterval(pollRef.current);
          navigate(`/play/${sessionId}`);
        }
      };
      wsRef.current = socket;
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Lobby WS error:', e); }

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (wsRef.current) wsRef.current.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);
  return (
    <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-900' : 'bg-gradient-to-br from-green-400 to-cyan-500'}`}>
      <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-3xl p-8 w-full max-w-md shadow-2xl text-center`}>
        <h1 className={`text-3xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-800'}`}>
          You're in! 🎉
        </h1>
        <p className={`text-xl mb-4 ${isDark ? 'text-green-400' : 'text-green-600'}`}>
          {playerInfo.player_name}
        </p>
        <p className={`mb-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          Waiting for host to start the game...
        </p>
        <div className="animate-pulse">
          <div className="flex justify-center space-x-2">
            <div className="w-4 h-4 bg-purple-500 rounded-full animate-bounce"></div>
            <div className="w-4 h-4 bg-pink-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
            <div className="w-4 h-4 bg-yellow-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
          </div>
        </div>
        <div className="mt-8">
          <p className={`text-sm ${isDark ? 'text-purple-300' : 'text-purple-300'}`}>
            {players.length} player{players.length !== 1 ? 's' : ''} in lobby
          </p>
        </div>
      </div>
    </div>
  );
};
// Game Play Component
const GamePlay = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const isHost = new URLSearchParams(window.location.search).get('host') === 'true';
  const playerInfo = JSON.parse(localStorage.getItem('player_info') || '{}');
  const [question, setQuestion] = useState(null);
  const [questionNumber, setQuestionNumber] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [timeLeft, setTimeLeft] = useState(20);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [correctAnswer, setCorrectAnswer] = useState(null);
  const [standings, setStandings] = useState([]);
  const [gameFinished, setGameFinished] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showCountdown, setShowCountdown] = useState(false);
  const [countdownValue, setCountdownValue] = useState(3);
  const [streak, setStreak] = useState(0);
  const [totalPlayers, setTotalPlayers] = useState(0);
  const [reactions, setReactions] = useState([]);
  const [rankings, setRankings] = useState([]);
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [lastPoints, setLastPoints] = useState(null);
  const pollRef = React.useRef(null);
  const wsRef = React.useRef(null);
  const lastQuestionRef = React.useRef(-1);

  // Polling: always runs to catch questions, results, game_finished
  const pollGameState = React.useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/games/${sessionId}/state`);
      if (!res.ok) return;
      const data = await res.json();

      if (data.status === 'finished' && data.rankings) {
        setGameFinished(true);
        setRankings(data.rankings);
        return;
      }

      // Handle showing_results state from polling (for when WebSocket fails)
      if (data.showing_results && !showResults) {
        setCorrectAnswer(data.correct_answer);
        setStandings(data.standings || []);
        setShowResults(true);
      }

      if (data.question && data.question_number > lastQuestionRef.current) {
        lastQuestionRef.current = data.question_number;
        setQuestion(data.question);
        setQuestionNumber(data.question_number);
        setTotalQuestions(data.total_questions);
        setTimeLeft(data.question.time_limit || 20);
        setSelectedAnswer(null);
        setShowResults(false);
        setCorrectAnswer(null);
        setQuestionStartTime(performance.now());
        setLastPoints(null);
      }
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Game poll error:', e); }
  }, [sessionId, showResults]);

  useEffect(() => {
    const playerId = isHost ? 'host' : playerInfo.player_id;
    if (!playerId && !isHost) { navigate('/join'); return; }

    // Always-on polling every 1.5s
    pollGameState(); // Immediate first check (picks up question sent by start_game)
    pollRef.current = setInterval(pollGameState, 1500);

    // Also try WebSocket for lower-latency updates
    try {
      const socket = new WebSocket(`${API_URL.replace('http', 'ws')}/api/ws/${sessionId}/${playerId}`);
      socket.onopen = () => { wsRef.current = socket; };
      socket.onmessage = (event) => {
        const msg = JSON.parse(event.data);
        if (msg.type === 'question') {
          lastQuestionRef.current = msg.question_number;
          setQuestion(msg.question);
          setQuestionNumber(msg.question_number);
          setTotalQuestions(msg.total_questions);
          setTimeLeft(msg.question.time_limit || 20);
          setSelectedAnswer(null);
          setShowResults(false);
          setCorrectAnswer(null);
          setQuestionStartTime(performance.now());
          setLastPoints(null);
        } else if (msg.type === 'question_results') {
          setCorrectAnswer(msg.correct_answer);
          setStandings(msg.standings);
          setShowResults(true);
        } else if (msg.type === 'game_finished') {
          setGameFinished(true);
          setRankings(msg.rankings);
        }
      };
      socket.onclose = () => { wsRef.current = null; };
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Game WS error:', e); }

    return () => {
      if (wsRef.current) wsRef.current.close();
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, isHost]);
  useEffect(() => {
    if (timeLeft > 0 && question && !showResults) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && isHost && !showResults) {
      showQuestionResults();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft, question, showResults, isHost]);
  const nextQuestion = async () => {
    await fetch(`${API_URL}/api/games/${sessionId}/next-question`, { method: 'POST' });
  };
  const showQuestionResults = async () => {
    await fetch(`${API_URL}/api/games/${sessionId}/show-results`, { method: 'POST' });
  };
  const submitAnswer = async (index) => {
    if (selectedAnswer !== null || isHost) return;
    setSelectedAnswer(index);
    // Calculate precise time taken using high-resolution timestamp
    const now = performance.now();
    const timeTakenMs = questionStartTime ? now - questionStartTime : 0;
    const timeTakenSeconds = timeTakenMs / 1000; // Convert to seconds with millisecond precision
    const res = await fetch(
      `${API_URL}/api/games/${sessionId}/answer?player_id=${playerInfo.player_id}&answer_index=${index}&time_taken=${timeTakenSeconds.toFixed(3)}`,
      { method: 'POST' }
    );
    const data = await res.json();
    setLastPoints(data.points);
  };
  const colors = ['bg-quiz-red', 'bg-quiz-blue', 'bg-quiz-yellow', 'bg-quiz-green'];
  const shapes = ['▲', '◆', '●', '■'];
  if (gameFinished) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gradient-to-br from-purple-600 to-pink-500'} py-12 px-4`}>
        <div className="max-w-2xl mx-auto">
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-3xl p-8 shadow-2xl text-center`}>
            <h1 className="text-4xl font-bold mb-2">🏆</h1>
            <h2 className={`text-3xl font-bold mb-8 ${isDark ? 'text-white' : 'text-gray-800'}`}>
              Game Over!
            </h2>
            <div className="space-y-4 mb-8">
              {rankings.slice(0, 5).map((player, i) => (
                <div
                  key={player.player_id}
                  className={`flex items-center justify-between p-4 rounded-xl ${
                    i === 0 ? 'bg-yellow-100 dark:bg-yellow-900/30' :
                    i === 1 ? 'bg-gray-100 dark:bg-gray-700' :
                    i === 2 ? 'bg-orange-100 dark:bg-orange-900/30' :
                    isDark ? 'bg-gray-700' : 'bg-gray-50'
                  }`}
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <div className="flex items-center space-x-4">
                    <span className="text-2xl font-bold">
                      {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
                    </span>
                    <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>
                      {player.name}
                    </span>
                  </div>
                  <span className="font-bold text-purple-600">{player.score} pts</span>
                </div>
              ))}
            </div>
            <button
              onClick={() => navigate(isHost ? '/dashboard' : '/')}
              className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold rounded-xl hover:scale-105 transition-transform"
            >
              {isHost ? 'Back to Dashboard' : 'Play Again'}
            </button>
          </div>
        </div>
      </div>
    );
  }
  if (!question) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0f0529]' : 'bg-purple-600'}`}>
        <div className="text-white text-xl">Loading question...</div>
      </div>
    );
  }
  // 3-2-1-GO! countdown overlay
  if (showCountdown) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0f0529]' : 'bg-gradient-to-br from-[#1a0d3e] via-[#2d1b69] to-[#0f0529]'}`}>
        <div className="text-center countdown-animate" key={countdownValue}>
          <div className="text-9xl font-black text-white drop-shadow-2xl">
            {countdownValue > 0 ? countdownValue : 'GO!'}
          </div>
          <p className={`text-xl mt-4 ${isDark ? 'text-purple-300' : 'text-white/70'}`}>
            Question {questionNumber} of {totalQuestions}
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0f0529]' : 'bg-[#352770]'} p-4`}>
      <Confetti active={showConfetti} />
      {/* Floating emoji reactions */}
      {reactions.map(r => (
        <div key={r.id} className="fixed text-4xl pointer-events-none emoji-float z-50" style={{ left: `${r.x}%`, bottom: '20%' }}>
          {r.emoji}
        </div>
      ))}
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <span className={`text-lg font-medium ${isDark ? 'text-purple-200' : 'text-purple-200'}`}>
              Question {questionNumber} of {totalQuestions}
            </span>
            {streak >= 2 && !isHost && (
              <span className="streak-badge px-3 py-1 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full text-sm font-bold">
                {streak >= 5 ? '🔥🔥🔥' : streak >= 3 ? '🔥🔥' : '🔥'} {streak} streak!
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <MuteButton />
            <div className={`text-4xl font-bold ${timeLeft <= 5 ? 'text-red-500 animate-pulse' : isDark ? 'text-white' : 'text-white'}`}>
              {timeLeft}
            </div>
          </div>
        </div>
        {/* Question */}
        <div className={`${isDark ? 'bg-[#241651]' : 'bg-[#241651]'} rounded-3xl p-8 mb-6 shadow-lg`}>
          {/* Question Image/GIF if exists */}
          {question.image_url && (
            <div className="flex justify-center mb-6">
              <img 
                src={question.image_url} 
                alt="Question visual" 
                className="max-h-48 rounded-xl object-contain"
              />
            </div>
          )}
          <h2 className={`text-2xl md:text-3xl font-bold text-center ${isDark ? 'text-white' : 'text-gray-800'}`}>
            {question.text}
          </h2>
        </div>
        {/* Answers */}
        {!showResults ? (
          <div className="grid grid-cols-2 gap-4">
            {question.answers.map((answer, i) => (
              <button
                key={`ans-${i}`}
                onClick={() => submitAnswer(i)}
                disabled={selectedAnswer !== null || isHost}
                className={`${colors[i]} p-6 rounded-2xl text-white font-bold text-lg md:text-xl transition-all
                  ${selectedAnswer === i ? 'ring-4 ring-white scale-95' : 'hover:scale-105'}
                  ${selectedAnswer !== null && selectedAnswer !== i ? 'opacity-50' : ''}
                  disabled:cursor-not-allowed`}
                data-testid={`answer-btn-${i}`}
              >
                <span className="text-3xl mr-3">{shapes[i]}</span>
                {answer.text}
              </button>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            {question.answers.map((answer, i) => (
              <div
                key={`res-${i}`}
                className={`${colors[i]} p-6 rounded-2xl text-white font-bold text-lg md:text-xl relative
                  ${i === correctAnswer ? 'ring-4 ring-green-400' : 'opacity-50'}`}
              >
                <span className="text-3xl mr-3">{shapes[i]}</span>
                {answer.text}
                {i === correctAnswer && (
                  <span className="absolute top-2 right-2 text-2xl">✓</span>
                )}
              </div>
            ))}
          </div>
        )}
        {/* Points earned display for players */}
        {!isHost && selectedAnswer !== null && lastPoints !== null && !showResults && (
          <div className="mt-6 text-center animate-bounce">
            <div className={`inline-block px-8 py-4 rounded-2xl ${lastPoints > 0 ? 'bg-green-500' : 'bg-red-500'}`}>
              <p className="text-white text-2xl font-bold">
                {lastPoints > 0 ? `+${lastPoints} points!` : 'No points'}
              </p>
              {lastPoints > 1500 && (
                <p className="text-yellow-300 text-sm">⚡ Lightning fast!</p>
              )}
              {lastPoints > 1000 && lastPoints <= 1500 && (
                <p className="text-yellow-200 text-sm">🔥 Great speed!</p>
              )}
            </div>
          </div>
        )}
        {/* Host Controls - Always visible for host */}
        {isHost && (
          <div className={`mt-8 p-4 rounded-2xl ${isDark ? 'bg-[#241651] border border-[#352770]' : 'bg-[#241651] border border-[#3b2580]'} shadow-lg`}>
            <h3 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-white'}`}>
              🎮 Host Controls
            </h3>
            <div className="flex flex-wrap gap-4 justify-center">
              {/* Show Results Button - when timer is done or manually */}
              {!showResults && (
                <button
                  onClick={showQuestionResults}
                  className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-bold rounded-xl hover:scale-105 transition-transform"
                  data-testid="show-results-btn"
                >
                  📊 Show Results
                </button>
              )}
              {/* Next Question Button - always available */}
              <button
                onClick={nextQuestion}
                className={`px-6 py-3 text-white font-bold rounded-xl hover:scale-105 transition-transform ${
                  showResults 
                    ? 'bg-gradient-to-r from-green-500 to-emerald-600' 
                    : 'bg-gradient-to-r from-yellow-500 to-orange-500'
                }`}
                data-testid="next-question-btn"
              >
                {showResults ? 'Next Question →' : '⏩ Skip to Next'}
              </button>
            </div>
            {!showResults && timeLeft > 0 && (
              <p className={`text-sm mt-3 text-center ${isDark ? 'text-purple-300' : 'text-purple-300'}`}>
                Timer: {timeLeft}s remaining • Results will auto-show when timer ends
              </p>
            )}
          </div>
        )}
        {/* Standings */}
        {showResults && standings.length > 0 && (
          <div className={`mt-8 ${isDark ? 'bg-[#241651]' : 'bg-[#241651]'} rounded-2xl p-6`}>
            <h3 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-white'}`}>
              Current Standings
            </h3>
            <div className="space-y-2">
              {standings.slice(0, 5).map((player, i) => (
                <div key={player.player_id} className="flex justify-between items-center leaderboard-item" style={{ animationDelay: `${i * 0.1}s` }}>
                  <span className={isDark ? 'text-purple-200' : 'text-purple-200'}>
                    #{i + 1} {player.avatar_emoji || ''} {player.name}
                    {player.streak >= 2 && <span className="ml-1 text-orange-500">🔥{player.streak}</span>}
                  </span>
                  <span className="font-bold text-purple-500">{player.score} pts</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
const ReportIssueModal = ({ isOpen, onClose }) => {
  const { isDark } = useTheme();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [kind, setKind] = useState('bug');
  const [severity, setSeverity] = useState('medium');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const reset = () => {
    setTitle('');
    setDescription('');
    setKind('bug');
    setSeverity('medium');
    setResult(null);
    setError('');
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (title.trim().length < 3 || description.trim().length < 10) {
      setError('Please provide a title (3+ chars) and description (10+ chars).');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim(),
          kind,
          severity,
          path: window.location.pathname + window.location.search,
          user_agent: navigator.userAgent,
          app_version: process.env.REACT_APP_VERSION || 'dev',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || 'Could not submit. Please try again later.');
        return;
      }
      setResult(data);
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
      onClick={handleClose}
      data-testid="report-issue-modal"
    >
      <div
        className={`w-full max-w-lg rounded-2xl p-6 shadow-2xl ${isDark ? 'bg-[#241651] text-white' : 'bg-[#241651] text-white'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-2xl font-bold">Report an issue</h2>
            <p className="text-sm text-purple-200 mt-1">
              Tell us what's wrong and we'll get on it.
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-purple-200 hover:text-white text-2xl leading-none"
            aria-label="Close"
            data-testid="report-issue-close"
          >
            ×
          </button>
        </div>

        {result ? (
          <div className="space-y-4">
            <div className="rounded-lg bg-emerald-500/20 border border-emerald-400/40 p-4 text-emerald-100">
              <p className="font-semibold">Thanks — your report was filed.</p>
              {result.issue_url && (
                <p className="text-sm mt-1">
                  Tracked as{' '}
                  <a
                    href={result.issue_url}
                    target="_blank"
                    rel="noreferrer"
                    className="underline"
                  >
                    issue #{result.issue_number}
                  </a>
                  .
                </p>
              )}
            </div>
            <button
              onClick={handleClose}
              className="w-full px-4 py-2 bg-purple-500 hover:bg-purple-400 rounded-lg font-semibold"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={200}
                placeholder="Short summary"
                className="w-full px-3 py-2 rounded-lg bg-[#1a0d3e] border border-[#3b2580] text-white placeholder-purple-300 focus:outline-none focus:border-purple-400"
                data-testid="report-issue-title"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Type</label>
                <select
                  value={kind}
                  onChange={(e) => setKind(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-[#1a0d3e] border border-[#3b2580] text-white focus:outline-none focus:border-purple-400"
                  data-testid="report-issue-kind"
                >
                  <option value="bug">Bug</option>
                  <option value="feedback">Feedback</option>
                  <option value="feature">Feature request</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Severity</label>
                <select
                  value={severity}
                  onChange={(e) => setSeverity(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-[#1a0d3e] border border-[#3b2580] text-white focus:outline-none focus:border-purple-400"
                  data-testid="report-issue-severity"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={8000}
                rows={5}
                placeholder="What happened? Steps to reproduce, expected vs actual behaviour..."
                className="w-full px-3 py-2 rounded-lg bg-[#1a0d3e] border border-[#3b2580] text-white placeholder-purple-300 focus:outline-none focus:border-purple-400 resize-y"
                data-testid="report-issue-description"
                required
              />
            </div>
            {error && (
              <div className="rounded-lg bg-red-500/20 border border-red-400/40 p-3 text-sm text-red-100">
                {error}
              </div>
            )}
            <div className="text-xs text-purple-300">
              We'll attach your current page, browser, and account id (if signed in) so we
              can reproduce the issue.
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full px-4 py-2 bg-yellow-400 text-[#1a0d3e] font-bold rounded-lg hover:bg-yellow-300 disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="report-issue-submit"
            >
              {submitting ? 'Submitting…' : 'Submit report'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};


// Footer Component
const Footer = () => {
  const { isDark } = useTheme();
  const [showReport, setShowReport] = useState(false);
  return (
    <>
    <footer className={`py-8 px-4 border-t ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-gray-50 border-gray-200'}`}>
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center space-x-2">
            <span className="text-xl font-bold bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
              KwizHub
            </span>
            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              © {new Date().getFullYear()} All rights reserved.
            </span>
          </div>
          <div className="flex items-center space-x-6">
            <a
              href="/pricing"
              className={`text-sm hover:underline ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
              data-testid="footer-pricing-link"
            >
              Pricing
            </a>
            <a
              href="/terms"
              className={`text-sm hover:underline ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
              data-testid="footer-terms-link"
            >
              Terms of Service
            </a>
            <a
              href="/privacy"
              className={`text-sm hover:underline ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
              data-testid="footer-privacy-link"
            >
              Privacy Policy
            </a>
            <a
              href="/refund"
              className={`text-sm hover:underline ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
              data-testid="footer-refund-link"
            >
              Refund Policy
            </a>
            <button
              type="button"
              onClick={() => setShowReport(true)}
              className={`text-sm hover:underline ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
              data-testid="footer-report-issue-btn"
            >
              Report Issue
            </button>
          </div>
        </div>
      </div>
    </footer>
      <ReportIssueModal isOpen={showReport} onClose={() => setShowReport(false)} />
    </>
  );
};
// Terms of Service Page
const TermsPage = () => {
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <nav className={`fixed w-full top-0 z-50 border-b ${isDark ? 'bg-gray-900/95 border-gray-800' : 'bg-white/95 border-gray-200'} backdrop-blur-sm`}>
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <button onClick={() => navigate('/')} className="flex items-center space-x-2">
            <span className="text-2xl font-bold bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
              KwizHub
            </span>
          </button>
          <button
            onClick={toggleTheme}
            className={`p-2 rounded-full ${isDark ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'} transition-colors`}
            data-testid="theme-toggle-terms"
          >
            {isDark ? '☀️' : '🌙'}
          </button>
        </div>
      </nav>
      {/* Content */}
      <main className="pt-24 pb-16 px-4">
        <div className={`max-w-4xl mx-auto ${isDark ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-xl p-8 md:p-12`}>
          <h1 className={`text-3xl md:text-4xl font-bold mb-8 ${isDark ? 'text-white' : 'text-gray-900'}`} data-testid="terms-title">
            Terms of Service
          </h1>
          <div className={`prose max-w-none ${isDark ? 'prose-invert' : ''}`}>
            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              <strong>Last Updated:</strong> December 2025
            </p>
            <section className="mb-8">
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>1. Acceptance of Terms</h2>
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                By accessing or using KwizHub ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our service. KwizHub is a SaaS quiz platform that allows users to create, host, and participate in interactive quizzes.
              </p>
            </section>
            <section className="mb-8">
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>2. Description of Service</h2>
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                KwizHub provides a platform for creating quizzes through various methods including manual creation, PDF upload with AI extraction, and AI-generated content from topics. Users can host live multiplayer quiz sessions and compete in real-time.
              </p>
            </section>
            <section className="mb-8">
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>3. User Accounts</h2>
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                To access certain features, you must create an account using Google authentication. You are responsible for maintaining the confidentiality of your account and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use.
              </p>
            </section>
            <section className="mb-8">
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>4. User Content</h2>
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                You retain ownership of content you create on KwizHub. By uploading content, you grant us a non-exclusive license to use, display, and distribute your content as necessary to provide the Service. You are solely responsible for ensuring your content does not violate any third-party rights or applicable laws.
              </p>
            </section>
            <section className="mb-8">
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>5. Acceptable Use</h2>
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                You agree not to use KwizHub to: upload harmful, offensive, or illegal content; infringe on intellectual property rights; attempt to hack or disrupt the service; use automated systems to access the service without permission; or engage in any activity that could damage our reputation or service.
              </p>
            </section>
            <section className="mb-8">
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>6. Payment Terms</h2>
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                KwizHub may offer premium features through paid subscriptions. Payments are processed securely through our payment partner Lemon Squeezy. By subscribing, you authorize us to charge your payment method on a recurring basis until you cancel. All fees are stated in USD unless otherwise specified.
              </p>
            </section>
            <section className="mb-8">
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>7. Termination</h2>
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                We reserve the right to suspend or terminate your account at any time for violation of these terms. You may also delete your account at any time. Upon termination, your right to use the Service will immediately cease.
              </p>
            </section>
            <section className="mb-8">
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>8. Limitation of Liability</h2>
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                KwizHub is provided "as is" without warranties of any kind. We shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the Service. Our total liability shall not exceed the amount paid by you in the twelve months preceding the claim.
              </p>
            </section>
            <section className="mb-8">
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>9. Changes to Terms</h2>
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                We may modify these terms at any time. We will notify users of significant changes via email or through the Service. Your continued use after changes constitutes acceptance of the modified terms.
              </p>
            </section>
            <section className="mb-8">
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>10. Contact Information</h2>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                For questions about these Terms of Service, please contact us at support@kwizhub.com.
              </p>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};
// Privacy Policy Page
const PrivacyPolicyPage = () => {
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <nav className={`fixed w-full top-0 z-50 border-b ${isDark ? 'bg-gray-900/95 border-gray-800' : 'bg-white/95 border-gray-200'} backdrop-blur-sm`}>
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <button onClick={() => navigate('/')} className="flex items-center space-x-2">
            <span className="text-2xl font-bold bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
              KwizHub
            </span>
          </button>
          <button
            onClick={toggleTheme}
            className={`p-2 rounded-full ${isDark ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'} transition-colors`}
            data-testid="theme-toggle-privacy"
          >
            {isDark ? '☀️' : '🌙'}
          </button>
        </div>
      </nav>
      {/* Content */}
      <main className="pt-24 pb-16 px-4">
        <div className={`max-w-4xl mx-auto ${isDark ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-xl p-8 md:p-12`}>
          <h1 className={`text-3xl md:text-4xl font-bold mb-8 ${isDark ? 'text-white' : 'text-gray-900'}`} data-testid="privacy-title">
            Privacy Policy
          </h1>
          <div className={`prose max-w-none ${isDark ? 'prose-invert' : ''}`}>
            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              <strong>Last Updated:</strong> December 2025
            </p>
            <section className="mb-8">
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>1. Introduction</h2>
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                KwizHub ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our quiz platform service.
              </p>
            </section>
            <section className="mb-8">
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>2. Information We Collect</h2>
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <strong>Account Information:</strong> When you sign up using Google authentication, we receive your name, email address, and profile picture from Google.
              </p>
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <strong>User Content:</strong> We store quizzes you create, including questions, answers, and any uploaded PDF documents for processing.
              </p>
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <strong>Usage Data:</strong> We collect information about how you interact with our service, including game sessions, scores, and feature usage.
              </p>
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <strong>Payment Information:</strong> When you subscribe to premium features, payment processing is handled by Lemon Squeezy. We do not store your complete payment card details.
              </p>
            </section>
            <section className="mb-8">
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>3. How We Use Your Information</h2>
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                We use your information to: provide and maintain our Service; process your transactions; send you updates about your account; improve our Service; respond to your inquiries; and comply with legal obligations.
              </p>
            </section>
            <section className="mb-8">
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>4. AI Processing</h2>
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                When you use our AI features (PDF quiz generation or topic-based generation), your content is processed using third-party AI services. This data is used solely for generating quiz content and is not retained by our AI providers beyond the processing session.
              </p>
            </section>
            <section className="mb-8">
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>5. Data Sharing</h2>
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                We do not sell your personal information. We may share data with: service providers who assist in operating our platform; payment processor (Lemon Squeezy) for transaction processing; and legal authorities when required by law.
              </p>
            </section>
            <section className="mb-8">
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>6. Data Security</h2>
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                We implement appropriate technical and organizational measures to protect your personal information. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>
            <section className="mb-8">
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>7. Your Rights</h2>
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                You have the right to: access your personal data; correct inaccurate data; request deletion of your data; export your data; and withdraw consent for data processing. To exercise these rights, contact us at privacy@kwizhub.com.
              </p>
            </section>
            <section className="mb-8">
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>8. Cookies and Tracking</h2>
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                We use essential cookies to maintain your session and preferences. We may use analytics tools to understand how users interact with our service. You can control cookie settings through your browser.
              </p>
            </section>
            <section className="mb-8">
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>9. Children's Privacy</h2>
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                KwizHub is not intended for children under 13. We do not knowingly collect personal information from children under 13. If you believe we have collected such information, please contact us immediately.
              </p>
            </section>
            <section className="mb-8">
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>10. Changes to This Policy</h2>
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last Updated" date.
              </p>
            </section>
            <section className="mb-8">
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>11. Contact Us</h2>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                If you have questions about this Privacy Policy, please contact us at privacy@kwizhub.com.
              </p>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};
// Refund Policy Page
const RefundPolicyPage = () => {
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <nav className={`fixed w-full top-0 z-50 border-b ${isDark ? 'bg-gray-900/95 border-gray-800' : 'bg-white/95 border-gray-200'} backdrop-blur-sm`}>
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <button onClick={() => navigate('/')} className="flex items-center space-x-2">
            <span className="text-2xl font-bold bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
              KwizHub
            </span>
          </button>
          <button
            onClick={toggleTheme}
            className={`p-2 rounded-full ${isDark ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'} transition-colors`}
            data-testid="theme-toggle-refund"
          >
            {isDark ? '☀️' : '🌙'}
          </button>
        </div>
      </nav>
      {/* Content */}
      <main className="pt-24 pb-16 px-4">
        <div className={`max-w-4xl mx-auto ${isDark ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-xl p-8 md:p-12`}>
          <h1 className={`text-3xl md:text-4xl font-bold mb-8 ${isDark ? 'text-white' : 'text-gray-900'}`} data-testid="refund-title">
            Refund Policy
          </h1>
          <div className={`prose max-w-none ${isDark ? 'prose-invert' : ''}`}>
            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              <strong>Last Updated:</strong> December 2025
            </p>
            <section className="mb-8">
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>1. Overview</h2>
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                At KwizHub, we want you to be completely satisfied with your purchase. This Refund Policy outlines our guidelines for refunds on subscription services purchased through our platform.
              </p>
            </section>
            <section className="mb-8">
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>2. Free Trial</h2>
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                We offer a free tier with limited features so you can try KwizHub before subscribing to a paid plan. We encourage all users to fully explore the free tier before upgrading.
              </p>
            </section>
            <section className="mb-8">
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>3. Subscription Refunds</h2>
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <strong>Monthly Subscriptions:</strong> You may request a full refund within 7 days of your initial purchase or renewal if you have not extensively used premium features during that period.
              </p>
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <strong>Annual Subscriptions:</strong> You may request a full refund within 14 days of your initial purchase. After 14 days, we may offer a prorated refund at our discretion.
              </p>
            </section>
            <section className="mb-8">
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>4. How to Request a Refund</h2>
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                To request a refund, please contact our support team at billing@kwizhub.com with:
              </p>
              <ul className={`list-disc pl-6 mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li>Your account email address</li>
                <li>Date of purchase</li>
                <li>Reason for the refund request</li>
                <li>Transaction ID or receipt (if available)</li>
              </ul>
            </section>
            <section className="mb-8">
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>5. Processing Time</h2>
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                We aim to process all refund requests within 5-7 business days. Once approved, the refund will be issued to your original payment method. Depending on your payment provider (Lemon Squeezy), it may take an additional 5-10 business days to appear in your account.
              </p>
            </section>
            <section className="mb-8">
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>6. Non-Refundable Items</h2>
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                The following are not eligible for refunds:
              </p>
              <ul className={`list-disc pl-6 mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li>Requests made after the refund eligibility period</li>
                <li>Accounts that have violated our Terms of Service</li>
                <li>Subscriptions with significant usage of premium features</li>
                <li>Partial months of service after cancellation</li>
              </ul>
            </section>
            <section className="mb-8">
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>7. Cancellation vs. Refund</h2>
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Canceling your subscription is different from requesting a refund. When you cancel, your subscription remains active until the end of your current billing period, but you will not be charged again. A refund returns money for time already paid.
              </p>
            </section>
            <section className="mb-8">
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>8. Disputes</h2>
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                If you have a payment dispute, please contact us before initiating a chargeback with your payment provider. We are committed to resolving issues fairly and promptly.
              </p>
            </section>
            <section className="mb-8">
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>9. Changes to This Policy</h2>
              <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                We reserve the right to modify this Refund Policy at any time. Changes will be effective immediately upon posting to this page.
              </p>
            </section>
            <section className="mb-8">
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>10. Contact Us</h2>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                For refund requests or questions about this policy, please contact us at billing@kwizhub.com.
              </p>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};
// Upgrade Modal Component
const UpgradeModal = ({ isOpen, onClose, feature, currentTier }) => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  if (!isOpen) return null;
  const tierMessages = {
    pdf: "PDF uploads require a Pro subscription or Event Pass.",
    ai_credits: "You've used all your AI topic credits this month.",
    participants: "Your current plan doesn't support this many participants."
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="upgrade-modal">
      <div className={`max-w-lg w-full mx-4 ${isDark ? 'bg-[#241651]' : 'bg-[#241651]'} rounded-2xl shadow-2xl p-8`}>
        <div className="text-center">
          <div className="text-5xl mb-4">🚀</div>
          <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-white'}`}>
            Upgrade to Unlock
          </h2>
          <p className={`mb-6 ${isDark ? 'text-purple-200' : 'text-purple-200'}`}>
            {tierMessages[feature] || "Upgrade your plan to access this feature."}
          </p>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <button
              onClick={() => { onClose(); navigate('/pricing'); }}
              className="px-6 py-3 bg-gradient-to-r from-violet-500 to-purple-600 text-white font-semibold rounded-xl hover:scale-105 transition-transform"
              data-testid="upgrade-pro-btn"
            >
              View Plans
            </button>
            <button
              onClick={onClose}
              className={`px-6 py-3 rounded-xl font-semibold ${isDark ? 'bg-[#352770] text-white hover:bg-[#352770]' : 'bg-[#352770] text-white hover:bg-[#3b2580]'} transition-colors`}
              data-testid="upgrade-close-btn"
            >
              Maybe Later
            </button>
          </div>
          <p className={`text-sm ${isDark ? 'text-purple-300' : 'text-purple-300'}`}>
            Current plan: <span className="font-semibold capitalize">{currentTier || 'Free'}</span>
          </p>
        </div>
      </div>
    </div>
  );
};
// Pricing Page Component
const PricingPage = () => {
  const { isDark, toggleTheme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [userTier, setUserTier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBilling, setSelectedBilling] = useState('yearly');
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (user) {
          const tierRes = await authFetch(`${API_URL}/api/account/tier`);
          if (tierRes.ok) {
            const tierData = await tierRes.json();
            setUserTier(tierData);
          }
        }
      } catch (err) {
        console.error('Failed to fetch tier:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);
  const handleUpgrade = async (tier) => {
    if (!user) {
      navigate('/auth');
      return;
    }
    
    try {
      // Determine the plan based on tier and billing cycle
      let plan;
      if (tier === 'event') {
        plan = 'big_event';
      } else if (selectedBilling === 'yearly') {
        plan = 'pro_annual';
      } else {
        plan = 'pro_monthly';
      }
      
      // Create checkout session
      const response = await authFetch(`${API_URL}/api/checkout/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan: plan,
          user_id: user.user_id
        })
      });
      
      const data = await response.json();
      
      if (data.checkout_url) {
        // Redirect to Lemon Squeezy checkout
        window.location.href = data.checkout_url;
      } else {
        alert('Unable to create checkout session. Please try again.');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Payment system error. Please try again later.');
    }
  };
  // Pricing data
  const pricingData = {
    free: {
      name: 'Open Access',
      price: 0,
      priceYearly: 0,
      period: 'Free forever',
      description: 'Perfect for trying out KwizHub',
      features: [
        { text: 'Up to 50 participants per game', included: true },
        { text: 'Unlimited manual quiz creation', included: true },
        { text: '3 AI topic quizzes per month', included: true },
        { text: 'Real-time multiplayer games', included: true },
        { text: 'Basic game analytics', included: true },
        { text: 'PDF quiz generation', included: false },
        { text: 'Custom branding', included: false },
        { text: 'Live moderation tools', included: false },
      ]
    },
    pro: {
      name: 'Pro Learner',
      price: 7.99,
      priceYearly: 4.99,
      period: 'per month',
      description: 'For educators and teams who need more',
      popular: true,
      features: [
        { text: 'Up to 250 participants per game', included: true },
        { text: 'Unlimited manual quiz creation', included: true },
        { text: 'Unlimited AI topic quizzes', included: true },
        { text: 'Unlimited PDF quiz generation', included: true },
        { text: 'Advanced game analytics', included: true },
        { text: 'Custom branding & logo', included: true },
        { text: 'Priority email support', included: true },
        { text: 'Live moderation tools', included: false },
      ]
    },
    event: {
      name: 'Big Event Pass',
      price: 14.99,
      priceYearly: 14.99,
      period: 'one-time • 48 hours',
      description: 'For conferences, workshops & large events',
      features: [
        { text: 'Up to 1,000 participants per game', included: true },
        { text: 'All Pro features included', included: true },
        { text: 'Live moderation tools', included: true },
        { text: 'Kick/mute disruptive players', included: true },
        { text: 'Pause & resume games', included: true },
        { text: 'Priority server access', included: true },
        { text: 'Real-time player management', included: true },
        { text: '48-hour access window', included: true },
      ]
    }
  };
  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0f0529]' : 'bg-[#1a0d3e]'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }
  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0f0529]' : 'bg-gradient-to-br from-[#1a0d3e] via-[#2d1b69] to-[#0f0529]'}`}>
      {/* Header */}
      <nav className={`fixed w-full top-0 z-50 border-b ${isDark ? 'bg-[#0f0529]/95 border-[#352770]' : 'bg-white/20 border-white/20'} backdrop-blur-sm`}>
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <button onClick={() => navigate('/')} className="flex items-center space-x-2">
            <span className="text-2xl font-bold bg-gradient-to-r from-amber-300 to-yellow-400 bg-clip-text text-transparent">
              KwizHub
            </span>
          </button>
          <div className="flex items-center space-x-4">
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-full ${isDark ? 'bg-[#241651] hover:bg-[#352770]' : 'bg-white/20 hover:bg-white/30'} transition-colors`}
            >
              {isDark ? '☀️' : '🌙'}
            </button>
            {user ? (
              <button onClick={() => navigate('/dashboard')} className="px-4 py-2 bg-[#241651] text-purple-300 font-semibold rounded-full">
                Dashboard
              </button>
            ) : (
              <button onClick={() => navigate('/auth')} className="px-4 py-2 bg-[#241651] text-purple-300 font-semibold rounded-full">
                Sign In
              </button>
            )}
          </div>
        </div>
      </nav>
      {/* Content */}
      <main className="pt-24 pb-16 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" data-testid="pricing-title">
              Simple, Transparent Pricing
            </h1>
            <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
              No per-seat pricing. No hidden fees. Choose the plan that fits your needs.
            </p>
            {/* Billing Toggle */}
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full p-1.5">
              <button
                onClick={() => setSelectedBilling('monthly')}
                className={`px-6 py-2.5 rounded-full font-semibold transition-all ${selectedBilling === 'monthly' ? 'bg-white text-purple-600 shadow-lg' : 'text-white hover:bg-white/10'}`}
 
              >
                Monthly
              </button>
              <button
                onClick={() => setSelectedBilling('yearly')}
                className={`px-6 py-2.5 rounded-full font-semibold transition-all flex items-center gap-2 ${selectedBilling === 'yearly' ? 'bg-white text-purple-600 shadow-lg' : 'text-white hover:bg-white/10'}`}
              >
                Yearly
                <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">Save 37%</span>
              </button>
            </div>
          </div>
          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {/* Free Tier */}
            <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-3xl p-8 shadow-xl flex flex-col`} data-testid="pricing-free">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">🎯</span>
                </div>
                <h3 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {pricingData.free.name}
                </h3>
                <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {pricingData.free.description}
                </p>
                <div className="flex items-baseline justify-center">
                  <span className={`text-5xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>$0</span>
                </div>
                <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{pricingData.free.period}</p>
              </div>
              <ul className="space-y-3 mb-8 flex-grow">
                {pricingData.free.features.map((feature) => (
                  <li key={feature.text} className={`flex items-start ${feature.included ? (isDark ? 'text-gray-300' : 'text-gray-600') : (isDark ? 'text-gray-500' : 'text-gray-400')}`}>
                    <span className={`mr-3 mt-0.5 ${feature.included ? 'text-green-500' : 'text-gray-400'}`}>
                      {feature.included ? '✓' : '✕'}
                    </span>
                    <span className={!feature.included ? 'line-through' : ''}>{feature.text}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => !user && navigate('/auth')}
                disabled={userTier?.effective_tier === 'free' || user}
                className={`w-full py-3.5 rounded-xl font-semibold transition-all ${
                  userTier?.effective_tier === 'free' 
                    ? 'bg-green-500/20 text-green-300 cursor-default' 
                    : user 
                      ? 'bg-[#352770] text-purple-300 cursor-not-allowed'
                      : 'bg-[#352770] text-purple-300 hover:bg-[#3b2580]'
                }`}
              >
                {userTier?.effective_tier === 'free' ? '✓ Current Plan' : user ? 'Free Plan' : 'Get Started Free'}
              </button>
            </div>
            {/* Pro Tier */}
            <div className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-3xl p-8 shadow-2xl flex flex-col relative transform md:scale-105 z-10" data-testid="pricing-pro">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-yellow-400 text-[#1a0d3e] px-4 py-1.5 rounded-full text-sm font-bold shadow-lg">
                  ⭐ MOST POPULAR
                </span>
              </div>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">🚀</span>
                </div>
                <h3 className="text-2xl font-bold mb-2 text-white">
                  {pricingData.pro.name}
                </h3>
                <p className="text-sm mb-4 text-white/70">
                  {pricingData.pro.description}
                </p>
                <div className="flex items-baseline justify-center">
                  <span className="text-5xl font-bold text-white">
                    ${selectedBilling === 'yearly' ? pricingData.pro.priceYearly : pricingData.pro.price}
                  </span>
                  <span className="text-xl text-white/80 ml-1">/mo</span>
                </div>
                <p className="text-sm mt-1 text-white/60">
                  {selectedBilling === 'yearly' ? 'Billed $59.88 annually' : 'Billed monthly'}
                </p>
              </div>
              <ul className="space-y-3 mb-8 flex-grow">
                {pricingData.pro.features.map((feature) => (
                  <li key={feature.text} className={`flex items-start ${feature.included ? 'text-white' : 'text-white/50'}`}>
                    <span className={`mr-3 mt-0.5 ${feature.included ? 'text-yellow-300' : 'text-white/30'}`}>
                      {feature.included ? '✓' : '✕'}
                    </span>
                    <span className={!feature.included ? 'line-through' : ''}>{feature.text}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => handleUpgrade('pro')}
                disabled={userTier?.effective_tier === 'pro'}
                className={`w-full py-3.5 rounded-xl font-semibold transition-all ${
                  userTier?.effective_tier === 'pro' 
                    ? 'bg-white/30 text-white cursor-default' 
                    : 'bg-[#241651] text-purple-300 hover:shadow-lg hover:scale-[1.02]'
                }`}
              >
                {userTier?.effective_tier === 'pro' ? '✓ Current Plan' : 'Upgrade to Pro'}
              </button>
            </div>
            {/* Event Tier */}
            <div className={`${isDark ? 'bg-[#241651]' : 'bg-[#241651]'} rounded-3xl p-8 shadow-xl flex flex-col border-2 border-orange-400`} data-testid="pricing-event">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-orange-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">🎪</span>
                </div>
                <h3 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-white'}`}>
                  {pricingData.event.name}
                </h3>
                <p className={`text-sm mb-4 ${isDark ? 'text-purple-300' : 'text-purple-300'}`}>
                  {pricingData.event.description}
                </p>
                <div className="flex items-baseline justify-center">
                  <span className={`text-5xl font-bold text-orange-500`}>
                    ${pricingData.event.price}
                  </span>
                </div>
                <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{pricingData.event.period}</p>
              </div>
              <ul className="space-y-3 mb-8 flex-grow">
                {pricingData.event.features.map((feature) => (
                  <li key={feature.text} className={`flex items-start ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    <span className="text-orange-500 mr-3 mt-0.5">✓</span>
                    {feature.text}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => handleUpgrade('event')}
                className="w-full py-3.5 rounded-xl font-semibold bg-gradient-to-r from-orange-500 to-red-500 text-white hover:shadow-lg hover:scale-[1.02] transition-all"
              >
                Buy Event Pass
              </button>
            </div>
          </div>
          {/* Feature Comparison */}
          <div className={`mt-20 ${isDark ? 'bg-[#241651]' : 'bg-[#241651]'} rounded-3xl p-8 shadow-xl`}>
            <h2 className={`text-2xl font-bold text-center mb-8 ${isDark ? 'text-white' : 'text-white'}`}>
              Compare All Features
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={`border-b ${isDark ? 'border-[#352770]' : 'border-[#3b2580]'}`}>
                    <th className={`text-left py-4 px-4 ${isDark ? 'text-purple-200' : 'text-purple-200'}`}>Feature</th>
                    <th className={`text-center py-4 px-4 ${isDark ? 'text-purple-200' : 'text-purple-200'}`}>Free</th>
                    <th className="text-center py-4 px-4 text-purple-500 font-bold">Pro</th>
                    <th className={`text-center py-4 px-4 text-orange-500`}>Event Pass</th>
                  </tr>
                </thead>
                <tbody className={`${isDark ? 'text-purple-200' : 'text-purple-200'}`}>
                  <tr className={`border-b ${isDark ? 'border-[#352770]' : 'border-[#3b2580]'}`}>
                    <td className="py-4 px-4">Participants per game</td>
                    <td className="text-center py-4 px-4">50</td>
                    <td className="text-center py-4 px-4 font-semibold text-purple-500">250</td>
                    <td className="text-center py-4 px-4 font-semibold text-orange-500">1,000</td>
                  </tr>
                  <tr className={`border-b ${isDark ? 'border-[#352770]' : 'border-[#3b2580]'}`}>
                    <td className="py-4 px-4">Manual quiz creation</td>
                    <td className="text-center py-4 px-4 text-green-500">Unlimited</td>
                    <td className="text-center py-4 px-4 text-green-500">Unlimited</td>
                    <td className="text-center py-4 px-4 text-green-500">Unlimited</td>
                  </tr>
                  <tr className={`border-b ${isDark ? 'border-[#352770]' : 'border-[#3b2580]'}`}>
                    <td className="py-4 px-4">AI topic quizzes</td>
                    <td className="text-center py-4 px-4">3/month</td>
                    <td className="text-center py-4 px-4 text-green-500">Unlimited</td>
                    <td className="text-center py-4 px-4 text-green-500">Unlimited</td>
                  </tr>
                  <tr className={`border-b ${isDark ? 'border-[#352770]' : 'border-[#3b2580]'}`}>
                    <td className="py-4 px-4">PDF quiz generation</td>
                    <td className="text-center py-4 px-4 text-red-400">✕</td>
                    <td className="text-center py-4 px-4 text-green-500">Unlimited</td>
                    <td className="text-center py-4 px-4 text-green-500">Unlimited</td>
                  </tr>
                  <tr className={`border-b ${isDark ? 'border-[#352770]' : 'border-[#3b2580]'}`}>
                    <td className="py-4 px-4">Custom branding</td>
                    <td className="text-center py-4 px-4 text-red-400">✕</td>
                    <td className="text-center py-4 px-4 text-green-500">✓</td>
                    <td className="text-center py-4 px-4 text-green-500">✓</td>
                  </tr>
                  <tr className={`border-b ${isDark ? 'border-[#352770]' : 'border-[#3b2580]'}`}>
                    <td className="py-4 px-4">Live moderation tools</td>
                    <td className="text-center py-4 px-4 text-red-400">✕</td>
                    <td className="text-center py-4 px-4 text-red-400">✕</td>
                    <td className="text-center py-4 px-4 text-green-500">✓</td>
                  </tr>
                  <tr className={`border-b ${isDark ? 'border-[#352770]' : 'border-[#3b2580]'}`}>
                    <td className="py-4 px-4">Priority server</td>
                    <td className="text-center py-4 px-4 text-red-400">✕</td>
                    <td className="text-center py-4 px-4 text-red-400">✕</td>
                    <td className="text-center py-4 px-4 text-green-500">✓</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          {/* Why KwizHub Section */}
          <div className="mt-16 text-center">
            <h2 className="text-3xl font-bold text-white mb-8">Why Choose KwizHub?</h2>
            <div className="grid md:grid-cols-4 gap-6 max-w-5xl mx-auto">
              <div className={`${isDark ? 'bg-[#241651]' : 'bg-white/10'} rounded-2xl p-6 backdrop-blur-sm`}>
                <div className="text-4xl mb-3">👥</div>
                <h3 className="font-bold text-white mb-2">50 Free Players</h3>
                <p className="text-sm text-white/70">Generous free tier for small groups</p>
              </div>
              <div className={`${isDark ? 'bg-[#241651]' : 'bg-white/10'} rounded-2xl p-6 backdrop-blur-sm`}>
                <div className="text-4xl mb-3">💰</div>
                <h3 className="font-bold text-white mb-2">No Per-Seat Pricing</h3>
                <p className="text-sm text-white/70">One price, unlimited games</p>
              </div>
              <div className={`${isDark ? 'bg-[#241651]' : 'bg-white/10'} rounded-2xl p-6 backdrop-blur-sm`}>
                <div className="text-4xl mb-3">🤖</div>
                <h3 className="font-bold text-white mb-2">AI-Powered</h3>
                <p className="text-sm text-white/70">Generate quizzes instantly</p>
              </div>
              <div className={`${isDark ? 'bg-[#241651]' : 'bg-white/10'} rounded-2xl p-6 backdrop-blur-sm`}>
                <div className="text-4xl mb-3">⚡</div>
                <h3 className="font-bold text-white mb-2">Real-Time</h3>
                <p className="text-sm text-white/70">Live multiplayer gameplay</p>
              </div>
            </div>
          </div>
          {/* FAQ Section */}
          <div className={`mt-16 ${isDark ? 'bg-[#241651]' : 'bg-[#241651]'} rounded-3xl p-8 shadow-xl`}>
            <h2 className={`text-2xl font-bold text-center mb-8 ${isDark ? 'text-white' : 'text-white'}`}>
              Frequently Asked Questions
            </h2>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <div>
                <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-white'}`}>
                  Can I cancel anytime?
                </h3>
                <p className={`text-sm ${isDark ? 'text-purple-300' : 'text-purple-200'}`}>
                  Yes! Pro subscriptions can be cancelled anytime. You'll retain access until the end of your billing period.
                </p>
              </div>
              <div>
                <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-white'}`}>
                  What happens after the Event Pass expires?
                </h3>
                <p className={`text-sm ${isDark ? 'text-purple-300' : 'text-purple-200'}`}>
                  After 48 hours, you'll return to your previous plan (Free or Pro). Your quizzes are saved.
                </p>
              </div>
              <div>
                <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-white'}`}>
                  Do unused AI credits roll over?
                </h3>
                <p className={`text-sm ${isDark ? 'text-purple-300' : 'text-purple-200'}`}>
                  Yes! Free tier AI credits roll over each month. They accumulate until you use them.
                </p>
              </div>
              <div>
                <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-white'}`}>
                  Can I upgrade mid-subscription?
                </h3>
                <p className={`text-sm ${isDark ? 'text-purple-300' : 'text-purple-200'}`}>
                  Absolutely! Upgrade anytime and we'll prorate the difference.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};
// Account Page Component
const AccountPage = () => {
  const { isDark, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tierInfo, setTierInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoFile, setLogoFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    const fetchTierInfo = async () => {
      try {
        const res = await authFetch(`${API_URL}/api/account/tier`);
        if (res.ok) {
          const data = await res.json();
          setTierInfo(data);
        }
      } catch (err) {
        console.error('Failed to fetch tier info:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTierInfo();
  }, []);
  const handleLogoUpload = async () => {
    if (!logoFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('logo', logoFile);
      const res = await authFetch(`${API_URL}/api/account/branding`, {
        method: 'POST',
        body: formData
      });
      if (res.ok) {
        const data = await res.json();
        setTierInfo(prev => ({ ...prev, branding: { logo_url: data.logo_url } }));
        setLogoFile(null);
        alert('Logo uploaded successfully!');
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to upload logo');
      }
    } catch (err) {
      alert('Upload failed');
    } finally {
      setUploading(false);
    }
  };
  const handleRemoveLogo = async () => {
    try {
      const res = await authFetch(`${API_URL}/api/account/branding`, {
        method: 'DELETE'
      });
      if (res.ok) {
        setTierInfo(prev => ({ ...prev, branding: null }));
        alert('Logo removed');
      }
    } catch (err) {
      alert('Failed to remove logo');
    }
  };
  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0f0529]' : 'bg-[#1a0d3e]'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }
  const tier = tierInfo?.effective_tier || 'free';
  const isProOrEvent = tier === 'pro' || tier === 'event';
  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0f0529]' : 'bg-[#1a0d3e]'}`}>
      {/* Header */}
      <nav className={`fixed w-full top-0 z-50 border-b ${isDark ? 'bg-[#0f0529]/95 border-[#352770]' : 'bg-[#0f0529]/95 border-[#352770]'} backdrop-blur-sm`}>
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <button onClick={() => navigate('/')} className="flex items-center space-x-2">
            <span className="text-2xl font-bold bg-gradient-to-r from-amber-300 to-yellow-400 bg-clip-text text-transparent">
              KwizHub
            </span>
          </button>
          <div className="flex items-center space-x-4">
            <button onClick={toggleTheme} className={`p-2 rounded-full ${isDark ? 'bg-[#241651]' : 'bg-[#352770]'}`}>
              {isDark ? '☀️' : '🌙'}
            </button>
            <button onClick={() => navigate('/dashboard')} className={`px-4 py-2 rounded-full ${isDark ? 'bg-[#241651] text-white' : 'bg-[#352770] text-white'}`}>
              Dashboard
            </button>
          </div>
        </div>
      </nav>
      {/* Content */}
      <main className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className={`text-3xl font-bold mb-8 ${isDark ? 'text-white' : 'text-white'}`} data-testid="account-title">
            Account Settings
          </h1>
          {/* User Info Card */}
          <div className={`${isDark ? 'bg-[#241651]' : 'bg-[#241651]'} rounded-2xl p-6 mb-6 shadow-lg`}>
            <div className="flex items-center gap-4 mb-4">
              {user?.picture && (
                <img src={user.picture} alt="" className="w-16 h-16 rounded-full" />
              )}
              <div>
                <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-white'}`}>{user?.name}</h2>
                <p className={`${isDark ? 'text-purple-300' : 'text-purple-300'}`}>{user?.email}</p>
              </div>
            </div>
          </div>
          {/* Subscription Card */}
          <div className={`${isDark ? 'bg-[#241651]' : 'bg-[#241651]'} rounded-2xl p-6 mb-6 shadow-lg`} data-testid="subscription-card">
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-white'}`}>
              Your Subscription
            </h3>
            <div className="flex items-center justify-between mb-4">
              <div>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${tier === 'free' ? 'bg-[#352770] text-white' : tier === 'pro' ? 'bg-[#352770] text-purple-200' : 'bg-orange-500/20 text-orange-300'}`}>
                  {tierInfo?.tier_name || 'Open Access'}
                </span>
                {tierInfo?.expires_at && (
                  <p className={`text-sm mt-2 ${isDark ? 'text-purple-300' : 'text-purple-300'}`}>
                    Expires: {new Date(tierInfo.expires_at).toLocaleDateString()}
                  </p>
                )}
              </div>
              {tier === 'free' && (
                <button
                  onClick={() => navigate('/pricing')}
                  className="px-4 py-2 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-lg font-semibold hover:scale-105 transition-transform"
                >
                  Upgrade
                </button>
              )}
            </div>
            {/* Limits */}
            <div className={`grid grid-cols-2 gap-4 p-4 rounded-xl ${isDark ? 'bg-[#352770]' : 'bg-[#352770]'}`}>
              <div>
                <p className={`text-sm ${isDark ? 'text-purple-300' : 'text-purple-300'}`}>Participant Limit</p>
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-white'}`}>
                  {tierInfo?.limits?.participant_limit || 50}
                </p>
              </div>
              <div>
                <p className={`text-sm ${isDark ? 'text-purple-300' : 'text-purple-300'}`}>AI Topic Credits</p>
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-white'}`}>
                  {tierInfo?.limits?.ai_topic_unlimited ? '∞' : tierInfo?.credits?.ai_topic || 0}
                </p>
              </div>
            </div>
          </div>
          {/* Usage Stats */}
          <div className={`${isDark ? 'bg-[#241651]' : 'bg-[#241651]'} rounded-2xl p-6 mb-6 shadow-lg`} data-testid="usage-card">
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-white'}`}>
              Usage Statistics
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className={`p-4 rounded-xl ${isDark ? 'bg-[#352770]' : 'bg-[#352770]'}`}>
                <p className={`text-sm ${isDark ? 'text-purple-300' : 'text-purple-300'}`}>AI Quizzes Generated</p>
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-white'}`}>
                  {tierInfo?.credits?.total_ai_generated || 0}
                </p>
              </div>
              <div className={`p-4 rounded-xl ${isDark ? 'bg-[#352770]' : 'bg-[#352770]'}`}>
                <p className={`text-sm ${isDark ? 'text-purple-300' : 'text-purple-300'}`}>PDFs Uploaded</p>
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-white'}`}>
                  {tierInfo?.credits?.total_pdfs_uploaded || 0}
                </p>
              </div>
            </div>
          </div>
          {/* Custom Branding (Pro/Event only) */}
          <div className={`${isDark ? 'bg-[#241651]' : 'bg-[#241651]'} rounded-2xl p-6 mb-6 shadow-lg`} data-testid="branding-card">
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-white'}`}>
              Custom Branding
              {!isProOrEvent && <span className="ml-2 text-xs bg-purple-500 text-white px-2 py-0.5 rounded-full">Pro</span>}
            </h3>
            {isProOrEvent ? (
              <div>
                {tierInfo?.branding?.logo_url ? (
                  <div className="flex items-center gap-4">
                    <img src={tierInfo.branding.logo_url} alt="Your logo" className="w-24 h-24 object-contain rounded-lg border" />
                    <div>
                      <p className={`text-sm ${isDark ? 'text-purple-200' : 'text-purple-200'}`}>Your logo will appear in game lobbies</p>
                      <button onClick={handleRemoveLogo} className="text-red-500 text-sm mt-2 hover:underline">Remove logo</button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className={`text-sm mb-4 ${isDark ? 'text-purple-200' : 'text-purple-200'}`}>
                      Upload your logo to display in game lobbies and on quiz cards.
                    </p>
                    <div className="flex items-center gap-4">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setLogoFile(e.target.files[0])}
                        className={`text-sm ${isDark ? 'text-purple-200' : 'text-purple-200'}`}
                      />
                      <button
                        onClick={handleLogoUpload}
                        disabled={!logoFile || uploading}
                        className="px-4 py-2 bg-purple-500 text-white rounded-lg disabled:opacity-50"
                      >
                        {uploading ? 'Uploading...' : 'Upload'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className={`text-sm ${isDark ? 'text-purple-300' : 'text-purple-300'}`}>
                Upgrade to Pro to add your logo to game lobbies and quiz cards.
                <button onClick={() => navigate('/pricing')} className="text-purple-500 ml-2 hover:underline">
                  View plans →
                </button>
              </p>
            )}
          </div>
          {/* Danger Zone */}
          <div className={`${isDark ? 'bg-[#241651] border-red-900' : 'bg-[#241651] border-red-200'} rounded-2xl p-6 border-2 shadow-lg`}>
            <h3 className="text-lg font-semibold mb-4 text-red-500">Danger Zone</h3>
            <button
              onClick={() => { logout(); navigate('/'); }}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};
// Top-level error boundary so a thrown render doesn't blank the whole SPA.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    devLog('ErrorBoundary caught:', error, info);
  }
  handleReload = () => { window.location.reload(); };
  render() {
    if (this.state.hasError) {
      return (
        <div role="alert" className="min-h-screen flex items-center justify-center bg-[#352770] dark:bg-[#0f0529] px-4">
          <div className="max-w-md text-center bg-[#241651] dark:bg-[#241651] p-8 rounded-2xl shadow">
            <h1 className="text-2xl font-bold mb-3 text-white dark:text-white">Something broke</h1>
            <p className="text-purple-200 dark:text-purple-200 mb-6">
              We hit an unexpected error. Try reloading — if it happens again, please let us know.
            </p>
            <button
              type="button"
              onClick={this.handleReload}
              className="px-5 py-2 bg-purple-500 text-white rounded-lg font-semibold hover:bg-purple-600"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Main App Router
function AppRouter() {
  const location = useLocation();
  // Handle auth callback - check for session_token in hash (from Google OAuth callback)
  if (location.hash?.includes('session_token=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/join" element={<JoinGame />} />
      <Route path="/lobby/:sessionId" element={<GameLobby />} />
      <Route path="/play/:sessionId" element={<GamePlay />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/create-options" element={<ProtectedRoute><CreateQuizOptions /></ProtectedRoute>} />
      <Route path="/create" element={<ProtectedRoute><CreateQuiz /></ProtectedRoute>} />
      <Route path="/edit/:quizId" element={<ProtectedRoute><EditQuiz /></ProtectedRoute>} />
      <Route path="/upload" element={<ProtectedRoute><UploadPDF /></ProtectedRoute>} />
      <Route path="/ai-generate" element={<ProtectedRoute><AIGenerateQuiz /></ProtectedRoute>} />
      <Route path="/browse" element={<ProtectedRoute><BrowseQuizzes /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/host/:quizId" element={<ProtectedRoute><HostGame /></ProtectedRoute>} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/refund" element={<RefundPage />} />
      <Route path="/quiz/:quizId" element={<QuizShareView />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <SoundProvider>
          <AuthProvider>
            <BrowserRouter>
              <AppRouter />
            </BrowserRouter>
          </AuthProvider>
        </SoundProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
export default App;
;
